import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2, CalendarDays, ChevronDown, Copy, Download,
  Eye, EyeOff, FileSpreadsheet, FileText, Filter, Grid2X2, GripVertical, Link2, List,
  MapPin, Presentation, RefreshCw, RotateCcw, Save, Search, SlidersHorizontal, Sun,
  Trash2, Upload, X, XCircle,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { ProposalTabs } from "@/components/ProposalTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api, { API, formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useHoardingTypes } from "@/lib/hoardingTypes";
import { useAuth } from "@/lib/auth";
import { PLAN_FEATURES, hasPlanFeature } from "@/lib/planFeatures";

const DONUT_COLORS = ["#2563EB", "#3B82F6", "#60A5FA", "#1D4ED8", "#1E40AF", "#94A3B8", "#93C5FD"];
const SIZE_EXEMPT_TYPES_STORAGE_KEY = "hoardexa.proposals.sizeExemptMediaTypes";

const today = new Date().toISOString().slice(0, 10);
const monthLater = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

function dateTimeLocalValue(value = new Date(Date.now() + 7 * 86400000)) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDateTimeToISO(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

let createProposalDraft = null;
let consumedEditProposalKey = "";
const proposalReferenceCache = {
  options: null,
  clients: null,
  templates: null,
  arrangements: null,
  settings: null,
  partnerSettings: null,
};

const EMPTY_DETAILS = {
  client_id: null,
  client_name: "",
  brand_name: "",
  campaign_name: "",
  start_date: today,
  end_date: monthLater,
  proposal_expires_at: dateTimeLocalValue(),
  budget: "",
  notes: "",
};

const EMPTY_FILTERS = {
  city: "",
  cities: [],
  area: "",
  areas: [],
  media_types: [],
  size: "",
  sizes: [],
  size_exempt_media_types: [],
  width: "",
  height: "",
  price_min: "",
  price_max: "",
  illumination: "",
  landmark: "",
  tags: "",
  allow_intermittent: false,
  inventory_scope: "owned",
  sort_by: "",
  sort_dir: "asc",
};

const TEXT_STRATEGIES = ["replace_entire_text", "replace_value_after_label", "replace_currency_value", "append", "none"];
const IMAGE_STRATEGIES = ["replace_image_keep_box", "replace_image_keep_crop", "none"];
const SLIDE_TYPES = ["agency_cover", "client_intro", "campaign_overview", "hoarding_detail", "hoarding_grid", "pricing_summary", "revenue_summary", "cost_summary", "route_map", "terms", "closing", "unknown"];
const REPEAT_MODES = ["none", "per_hoarding", "per_group"];
const TEMPLATE_FIELD_OPTIONS = [
  { key: "client_name", label: "Client name", category: "client", type: "text" },
  { key: "advertiser_name", label: "Advertiser name", category: "client", type: "text" },
  { key: "brand_name", label: "Brand name", category: "client", type: "text" },
  { key: "campaign_name", label: "Campaign name", category: "campaign", type: "text" },
  { key: "proposal_date", label: "Proposal date", category: "campaign", type: "text" },
  { key: "start_date", label: "Campaign start date", category: "campaign", type: "text" },
  { key: "end_date", label: "Campaign end date", category: "campaign", type: "text" },
  { key: "campaign_duration", label: "Campaign duration", category: "campaign", type: "text" },
  { key: "notes", label: "Proposal notes", category: "campaign", type: "text" },
  { key: "hoarding_name", label: "Hoarding name", category: "inventory", type: "text" },
  { key: "hoarding_code", label: "Hoarding code", category: "inventory", type: "text" },
  { key: "hoarding_id", label: "Hoarding ID", category: "inventory", type: "text" },
  { key: "hoarding_location", label: "Hoarding location", category: "inventory", type: "text" },
  { key: "location", label: "Location", category: "inventory", type: "text" },
  { key: "address", label: "Address", category: "inventory", type: "text" },
  { key: "city", label: "City", category: "inventory", type: "text" },
  { key: "area", label: "Area", category: "inventory", type: "text" },
  { key: "landmark", label: "Landmark", category: "inventory", type: "text" },
  { key: "latitude", label: "Latitude", category: "inventory", type: "text" },
  { key: "longitude", label: "Longitude", category: "inventory", type: "text" },
  { key: "coordinates", label: "Latitude, Longitude", category: "inventory", type: "text" },
  { key: "route_segment", label: "Route segment", category: "inventory", type: "text" },
  { key: "proposal_city_label", label: "Proposal city label", category: "inventory", type: "text" },
  { key: "media_type", label: "Media type", category: "inventory", type: "text" },
  { key: "hoarding_size", label: "Hoarding size", category: "inventory", type: "text" },
  { key: "size", label: "Size", category: "inventory", type: "text" },
  { key: "width", label: "Width", category: "inventory", type: "text" },
  { key: "height", label: "Height", category: "inventory", type: "text" },
  { key: "orientation", label: "Orientation", category: "inventory", type: "text" },
  { key: "facing", label: "Facing", category: "inventory", type: "text" },
  { key: "direction", label: "Traffic direction", category: "inventory", type: "text" },
  { key: "illumination", label: "Illumination", category: "inventory", type: "text" },
  { key: "availability", label: "Availability", category: "inventory", type: "text" },
  { key: "occupancy_status", label: "Occupancy status", category: "inventory", type: "text" },
  { key: "display_charges", label: "Display charges", category: "pricing", type: "text" },
  { key: "printing_charges", label: "Printing charges", category: "pricing", type: "text" },
  { key: "mounting_charges", label: "Mounting charges", category: "pricing", type: "text" },
  { key: "monthly_price", label: "Monthly price", category: "pricing", type: "text" },
  { key: "total_amount", label: "Total amount", category: "pricing", type: "text" },
  { key: "net_amount", label: "Net amount", category: "pricing", type: "text" },
  { key: "subtotal", label: "Subtotal", category: "summary", type: "text" },
  { key: "tax", label: "GST / tax", category: "summary", type: "text" },
  { key: "grand_total", label: "Grand total", category: "summary", type: "text" },
  { key: "budget", label: "Budget", category: "summary", type: "text" },
  { key: "budget_used_pct", label: "Budget used %", category: "summary", type: "text" },
  { key: "remaining", label: "Remaining budget", category: "summary", type: "text" },
  { key: "hoarding_image", label: "Hoarding image", category: "image", type: "image" },
  { key: "map", label: "Map image", category: "image", type: "image" },
];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function cloneJSON(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

function toggleArray(values, value) {
  const current = asArray(values);
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

function parseMoneyInput(value) {
  return Number(String(value || "").replace(/[^\d.]/g, "") || 0);
}

function formatIndianDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-IN");
}

function amountToIndianWords(amount) {
  const n = Math.floor(parseMoneyInput(amount));
  if (n <= 999) return "";
  const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const belowHundred = (num) => num < 20 ? units[num] : `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${units[num % 10]}` : ""}`;
  const belowThousand = (num) => {
    const hundred = Math.floor(num / 100);
    const rest = num % 100;
    return `${hundred ? `${units[hundred]} Hundred` : ""}${hundred && rest ? " " : ""}${rest ? belowHundred(rest) : ""}`.trim();
  };
  const parts = [
    [Math.floor(n / 10000000), "Crore"],
    [Math.floor((n % 10000000) / 100000), "Lakh"],
    [Math.floor((n % 100000) / 1000), "Thousand"],
    [n % 1000, ""],
  ].filter(([num]) => num > 0);
  return parts.map(([num, label]) => `${belowThousand(num)}${label ? ` ${label}` : ""}`).join(" ");
}

function toPayload(details, filters, selectedIds = [], chargeEdits = {}, templateId = "", gst = null, arrangement = null) {
  const cleanFilters = {
    ...filters,
    city: "",
    area: "",
    status: "",
    cities: asArray(filters.cities),
    areas: asArray(filters.areas),
    media_types: asArray(filters.media_types),
    sizes: asArray(filters.sizes),
    size_exempt_media_types: asArray(filters.size_exempt_media_types),
    inventory_scope: selectedIds.length > 0 ? "all" : (filters.inventory_scope || "owned"),
    include_booked: false,
    orientation: "",
    statuses: [],
    width: filters.width === "" ? null : Number(filters.width),
    height: filters.height === "" ? null : Number(filters.height),
    price_min: filters.price_min === "" ? null : Number(filters.price_min),
    price_max: filters.price_max === "" ? null : Number(filters.price_max),
    illumination: filters.illumination === "" ? null : filters.illumination === "yes",
  };
  const cleanDetails = {
    ...details,
    proposal_expires_at: localDateTimeToISO(details.proposal_expires_at),
    budget: parseMoneyInput(details.budget),
  };
  const charges = Object.fromEntries(Object.entries(chargeEdits)
    .map(([id, value]) => [id, {
      display: value.display === "" || value.display == null ? null : parseMoneyInput(value.display),
      mounting: value.mounting === "" || value.mounting == null ? null : parseMoneyInput(value.mounting),
      printing: value.printing === "" || value.printing == null ? null : parseMoneyInput(value.printing),
    }])
    .filter(([, value]) => value.display != null || value.mounting != null || value.printing != null));
  return selectedIds.length > 0
    ? { details: cleanDetails, filters: cleanFilters, media_ids: selectedIds, charges, template_id: templateId || "", gst, arrangement }
    : { details: cleanDetails, filters: cleanFilters, gst, arrangement };
}

function proposalEditDraft(source) {
  const proposal = source?.proposal || source;
  if (!proposal?.id) return null;
  const details = proposal.details || {};
  const items = asArray(proposal.items);
  const chargeEdits = Object.fromEntries(items.map((item) => [item.id || item.hoarding_id, {
    display: item.display_charges || item.monthly_price || "",
    mounting: item.mounting_charges || "",
    printing: item.printing_charges || "",
  }]));
  const itemCache = Object.fromEntries(items.map((item) => [item.id || item.hoarding_id, item]));
  return {
    editProposalId: proposal.id,
    details: {
      client_id: details.client_id || null,
      client_name: details.client_name || proposal.client_name || "",
      brand_name: details.brand_name || proposal.brand_name || "",
      campaign_name: details.campaign_name || proposal.campaign_name || proposal.title || "",
      start_date: (details.start_date || "").slice(0, 10),
      end_date: (details.end_date || "").slice(0, 10),
      proposal_expires_at: dateTimeLocalValue(details.proposal_expires_at || proposal.proposal_expires_at),
      budget: details.budget ? Number(details.budget).toLocaleString("en-IN") : "",
      notes: details.notes || "",
    },
    filters: proposal.filters || EMPTY_FILTERS,
    items,
    itemCache,
    groups: [{ group_label: "Selected Media", items }],
    summary: proposal.summary || null,
    selected: items.map((item) => item.id || item.hoarding_id).filter(Boolean),
    chargeEdits,
    arrangement: proposal.arrangement || { type: "default", preset_id: "", manual_order: [] },
    gst: proposal.gst_details || { gst_mode: "exclusive", gst_percentage: 18, cgst_percentage: 9, sgst_percentage: 9, igst_percentage: 18, gst_treatment: "cgst_sgst", round_off_behavior: "none" },
    selectedTemplate: proposal.template_id || "",
    savedProposal: null,
    savedProposalSignature: "",
    centerTab: "selected",
  };
}

function chargeValue(item, edits, key) {
  const edit = edits[item.id]?.[key];
  if (edit !== undefined && edit !== "") return parseMoneyInput(edit);
  if (key === "display") return Number(item.default_display_charge ?? item.monthly_price ?? 0);
  if (key === "mounting") return Number(item.default_mounting_charge ?? 0);
  return Number(item.default_printing_charge ?? 0);
}

function itemProposalTotal(item, edits, months = 1) {
  return chargeValue(item, edits, "display") * months + chargeValue(item, edits, "mounting") + chargeValue(item, edits, "printing");
}

function selectedSummaryRows(items, edits, months, field, fallback = "Unknown") {
  const byKey = new Map();
  items.forEach((item) => {
    const key = String(item?.[field] || fallback).trim() || fallback;
    const current = byKey.get(key) || { key, count: 0, cost: 0 };
    current.count += 1;
    current.cost += itemProposalTotal(item, edits, months);
    byKey.set(key, current);
  });
  return [...byKey.values()].sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0));
}

function isCustomCharge(item, edits, key) {
  const value = edits[item.id]?.[key];
  return value !== undefined && value !== "";
}

function savedSizeExemptions() {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SIZE_EXEMPT_TYPES_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveSizeExemptions(values) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SIZE_EXEMPT_TYPES_STORAGE_KEY, JSON.stringify(asArray(values)));
}

function friendlyTemplateMappingError(value) {
  const text = String(value || "");
  if (!text.trim()) return "";
  if (/gemini|heuristic|http\s*\d+|api key|model|json|unavailable|high demand|preview rendering|hresult|exception|powershell|libreoffice|soffice|pdftoppm/i.test(text)) {
    return "Template format could not be prepared automatically. You can still review the detected fields manually.";
  }
  return text;
}

function isInternalTemplateWarning(value) {
  return /gemini|heuristic|http\s*\d+|api key|model|json|unavailable|high demand|replace=true|preview rendering|hresult|exception|powershell|libreoffice|soffice|pdftoppm/i.test(String(value || ""));
}

function filenameFromResponse(response, fallbackName) {
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallbackName;
}

function blobFromResponse(response) {
  return response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
}

function downloadBlob(response, fallbackName) {
  const blob = blobFromResponse(response);
  const filename = filenameFromResponse(response, fallbackName);
  downloadFileBlob(blob, filename);
}

function downloadFileBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function arrangementPayload(arrangement, selectedIds = []) {
  return {
    type: arrangement?.type || "default",
    preset_id: arrangement?.type === "custom_preset" ? arrangement?.preset_id || "" : "",
    manual_order: asArray(selectedIds),
  };
}

async function responseBlobError(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return formatApiError(parsed?.detail || parsed?.error || parsed?.message) || text;
    } catch {
      return "";
    }
  }
  return formatApiError(data?.detail || data?.error || data?.message) || err.message;
}

function RequiredLabel({ children }) {
  return <Label className="text-[12px] font-semibold text-[#334155]">{children} <span className="text-rose-500">*</span></Label>;
}

function clientMatchesQuery(client, query) {
  if (!query) return true;
  return [client.company, client.contact_person, client.mobile, client.email, client.business_type]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
}

function ClientNamePicker({ value, clients, loading = false, onChange, onPick }) {
  const [open, setOpen] = useState(false);
  const query = value.trim().toLowerCase();
  const visibleClients = useMemo(() => {
    const list = clients.filter((client) => clientMatchesQuery(client, query));
    return list.slice(0, 50);
  }, [clients, query]);

  return (
    <div className="relative">
      <Input
        value={value}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        placeholder="Type or select client"
        className="h-9 rounded-lg border-[#CBD5E1] pr-8 text-[13px]"
      />
      <Search size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-72 overflow-auto rounded-lg border border-[#CBD5E1] bg-white py-1 shadow-lg">
          {visibleClients.map((client) => (
            <button
              key={client.id}
              type="button"
              className="flex w-full flex-col px-3 py-2 text-left hover:bg-[#F1F5F9]"
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(client);
                setOpen(false);
              }}
            >
              <span className="truncate text-[13px] font-bold text-[#0F172A]">{client.company}</span>
              <span className="truncate text-[11px] font-semibold text-[#64748B]">
                {[client.contact_person, client.mobile, client.business_type].filter(Boolean).join(" | ") || "Client"}
              </span>
            </button>
          ))}
          {loading && (
            <div className="px-3 py-3 text-[12px] font-semibold text-[#64748B]">Searching clients...</div>
          )}
          {!loading && visibleClients.length === 0 && (
            <div className="px-3 py-3 text-[12px] font-semibold text-[#64748B]">No clients found</div>
          )}
        </div>
      )}
    </div>
  );
}

export function CreateProposal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const editDraft = proposalEditDraft(location.state?.editProposal);
  const editDraftKey = editDraft ? `${location.key || ""}:${editDraft.editProposalId}` : "";
  if (editDraft && consumedEditProposalKey !== editDraftKey && createProposalDraft?.editProposalId !== editDraft.editProposalId) {
    createProposalDraft = editDraft;
    consumedEditProposalKey = editDraftKey;
    window.history.replaceState({}, document.title);
  }
  const { names: businessMediaTypes, loading: loadingBusinessMediaTypes } = useHoardingTypes();
  const canProposalPdf = hasPlanFeature(user, PLAN_FEATURES.proposalPdf);
  const canProposalExcel = hasPlanFeature(user, PLAN_FEATURES.proposalExcel);
  const canProposalCustom = hasPlanFeature(user, PLAN_FEATURES.proposalCustom);
  const [details, setDetails] = useState(() => createProposalDraft?.details || EMPTY_DETAILS);
  const [filters, setFilters] = useState(() => {
    const baseFilters = { ...(createProposalDraft?.filters || EMPTY_FILTERS) };
    delete baseFilters.facing;
    delete baseFilters.facing_values;
    return {
      ...baseFilters,
      inventory_scope: baseFilters.inventory_scope === "partner" ? "partner" : "owned",
      size_exempt_media_types: asArray(createProposalDraft?.filters?.size_exempt_media_types).length > 0
        ? createProposalDraft.filters.size_exempt_media_types
        : savedSizeExemptions(),
    };
  });
  const [options, setOptions] = useState(() => createProposalDraft?.options || proposalReferenceCache.options || {});
  const [clients, setClients] = useState(() => createProposalDraft?.clients || proposalReferenceCache.clients || []);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [items, setItems] = useState(() => createProposalDraft?.items || []);
  const [itemCache, setItemCache] = useState(() => createProposalDraft?.itemCache || {});
  const [groups, setGroups] = useState(() => createProposalDraft?.groups || []);
  const [summary, setSummary] = useState(() => createProposalDraft?.summary || null);
  const [selected, setSelected] = useState(() => asArray(location.state?.selected).length > 0 ? asArray(location.state.selected) : (createProposalDraft?.selected || []));
  const [inventoryView, setInventoryView] = useState(() => createProposalDraft?.inventoryView || "list");
  const inventoryRef = useRef(null);
  const bottomSummaryRef = useRef(null);
  const filtersPanelRef = useRef(null);
  const selectAllMatchingRef = useRef(null);
  const savedProposalSignatureRef = useRef(createProposalDraft?.savedProposalSignature || "");
  const [centerTab, setCenterTab] = useState(() => createProposalDraft?.centerTab || "matching");
  const [searchQuery, setSearchQuery] = useState(() => createProposalDraft?.searchQuery || "");
  const [clientSort, setClientSort] = useState(() => createProposalDraft?.clientSort || "price_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => createProposalDraft?.pageSize || 12);
  const [selectedMediaPage, setSelectedMediaPage] = useState(1);
  const [selectedMediaPageSize, setSelectedMediaPageSize] = useState(10);
  const [showSharePassword, setShowSharePassword] = useState(false);
  const [expandedCharges, setExpandedCharges] = useState(() => new Set());
  const [chargeEdits, setChargeEdits] = useState(() => createProposalDraft?.chargeEdits || {});
  const [proposalTemplates, setProposalTemplates] = useState(() => createProposalDraft?.proposalTemplates || proposalReferenceCache.templates || []);
  const [arrangementPresets, setArrangementPresets] = useState(() => globalThis.__hoardexaProposalArrangementsCache || createProposalDraft?.arrangementPresets || proposalReferenceCache.arrangements || []);
  const [arrangement, setArrangement] = useState(() => createProposalDraft?.arrangement || { type: "default", preset_id: "", manual_order: [] });
  const [gst, setGst] = useState(() => createProposalDraft?.gst || proposalReferenceCache.settings || { gst_mode: "exclusive", gst_percentage: 18, cgst_percentage: 9, sgst_percentage: 9, igst_percentage: 18, gst_treatment: "cgst_sgst", round_off_behavior: "none" });
  const [selectedTemplate, setSelectedTemplate] = useState(() => createProposalDraft?.selectedTemplate || "");
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateFile, setTemplateFile] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateMapping, setTemplateMapping] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState("");
  const [showTemplateMappingEditor, setShowTemplateMappingEditor] = useState(false);
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState(null);
  const [deleteTemplateName, setDeleteTemplateName] = useState("");
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [bulkDisplay, setBulkDisplay] = useState("");
  const [bulkMounting, setBulkMounting] = useState("");
  const [bulkPrinting, setBulkPrinting] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [savedProposal, setSavedProposal] = useState(() => createProposalDraft?.savedProposal || null);
  const [shareLinks, setShareLinks] = useState(() => createProposalDraft?.shareLinks || []);
  const [sharing, setSharing] = useState(false);
  const [shareForm, setShareForm] = useState(() => createProposalDraft?.shareForm || {
    expires_at: expiryFromDays(7),
    allow_pdf_download: true,
    allow_excel_download: false,
    allow_ppt_download: false,
    show_prices: true,
    allow_client_response: true,
    password: "",
    internal_note: "",
  });
  const [partnerSettings, setPartnerSettings] = useState(() => createProposalDraft?.partnerSettings || proposalReferenceCache.partnerSettings || null);
  const [editProposalId, setEditProposalId] = useState(() => createProposalDraft?.editProposalId || "");

  useEffect(() => {
    return () => {
      if (filePreview?.url) window.URL.revokeObjectURL(filePreview.url);
    };
  }, [filePreview?.url]);

  useEffect(() => {
    if (proposalReferenceCache.options) {
      const nextOptions = proposalReferenceCache.options;
      setOptions(nextOptions);
      setFilters((prev) => ({
        ...prev,
        size_exempt_media_types: savedSizeExemptions(),
      }));
      return;
    }
    api.get("/proposals/filters/options", { skipGlobalLoader: true })
      .then(({ data }) => {
        const nextOptions = data || {};
        proposalReferenceCache.options = nextOptions;
        setOptions(nextOptions);
        setFilters((prev) => ({
          ...prev,
          size_exempt_media_types: savedSizeExemptions(),
        }));
      })
      .catch(() => setOptions({}));
  }, []);

  useEffect(() => {
    if (proposalReferenceCache.partnerSettings) {
      setPartnerSettings(proposalReferenceCache.partnerSettings);
      return;
    }
    api.get("/partner-network/settings", { skipGlobalLoader: true })
      .then(({ data }) => {
        proposalReferenceCache.partnerSettings = data || null;
        setPartnerSettings(proposalReferenceCache.partnerSettings);
      })
      .catch(() => setPartnerSettings(null));
  }, []);

  useEffect(() => {
    if (loadingBusinessMediaTypes) return;
    setOptions((prev) => ({ ...(prev || {}), media_types: businessMediaTypes }));
    setFilters((prev) => {
      const allowed = new Set(businessMediaTypes);
      const nextSizeExempt = savedSizeExemptions().filter((type) => allowed.has(type));
      const next = {
        ...prev,
        media_types: asArray(prev.media_types).filter((type) => allowed.has(type)),
        size_exempt_media_types: nextSizeExempt,
      };
      if (nextSizeExempt.length !== savedSizeExemptions().length) saveSizeExemptions(nextSizeExempt);
      return next;
    });
  }, [businessMediaTypes, loadingBusinessMediaTypes]);

  useEffect(() => {
    if (proposalReferenceCache.clients) {
      setClients(proposalReferenceCache.clients);
      return;
    }
    api.get("/clients", { params: { limit: 200 }, skipGlobalLoader: true })
      .then(({ data }) => {
        const nextClients = Array.isArray(data?.clients) ? data.clients : asArray(data);
        proposalReferenceCache.clients = nextClients;
        setClients(nextClients);
      })
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    const query = details.client_name.trim();
    if (!query) {
      if (proposalReferenceCache.clients) setClients(proposalReferenceCache.clients);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setClientSearchLoading(true);
      api.get("/clients", { params: { q: query, limit: 50 }, skipGlobalLoader: true })
        .then(({ data }) => {
          if (cancelled) return;
          const nextClients = Array.isArray(data?.clients) ? data.clients : asArray(data);
          setClients(nextClients);
        })
        .catch(() => {
          if (!cancelled) setClients([]);
        })
        .finally(() => {
          if (!cancelled) setClientSearchLoading(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [details.client_name]);

  useEffect(() => {
    if (!canProposalCustom) {
      setProposalTemplates([]);
      setSelectedTemplate("");
      return;
    }
    if (proposalReferenceCache.templates) {
      setProposalTemplates(proposalReferenceCache.templates);
      return;
    }
    api.get("/proposals/templates", { skipGlobalLoader: true })
      .then(({ data }) => {
        proposalReferenceCache.templates = asArray(data);
        setProposalTemplates(proposalReferenceCache.templates);
      })
      .catch(() => setProposalTemplates([]));
  }, [canProposalCustom]);

  useEffect(() => {
    if (!canProposalCustom) {
      setArrangementPresets([]);
      setArrangement({ type: "default", preset_id: "", manual_order: [] });
      return;
    }
    if (globalThis.__hoardexaProposalArrangementsCache) {
      setArrangementPresets(globalThis.__hoardexaProposalArrangementsCache);
      return;
    }
    if (proposalReferenceCache.arrangements) {
      setArrangementPresets(proposalReferenceCache.arrangements);
      return;
    }
    api.get("/proposals/arrangements", { skipGlobalLoader: true })
      .then(({ data }) => {
        proposalReferenceCache.arrangements = asArray(data);
        globalThis.__hoardexaProposalArrangementsCache = proposalReferenceCache.arrangements;
        setArrangementPresets(proposalReferenceCache.arrangements);
      })
      .catch(() => setArrangementPresets([]));
  }, [canProposalCustom]);

  useEffect(() => {
    if (createProposalDraft?.gst) return;
    if (proposalReferenceCache.settings) {
      setGst(proposalReferenceCache.settings);
      return;
    }
    api.get("/accounts/settings", { skipGlobalLoader: true })
      .then(({ data }) => {
        proposalReferenceCache.settings = {
          gst_mode: data?.default_gst_mode || "exclusive",
          gst_percentage: data?.default_gst_percentage || 18,
          cgst_percentage: data?.default_cgst_percentage || 9,
          sgst_percentage: data?.default_sgst_percentage || 9,
          igst_percentage: data?.default_igst_percentage || 18,
          gst_treatment: "cgst_sgst",
          round_off_behavior: data?.default_round_off || "none",
        };
        setGst(proposalReferenceCache.settings);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    createProposalDraft = {
      details,
      filters,
      options,
      clients,
      items,
      itemCache,
      groups,
      summary,
      selected,
      inventoryView,
      centerTab,
      searchQuery,
      clientSort,
      pageSize,
      chargeEdits,
      proposalTemplates,
      arrangementPresets,
      arrangement,
      gst,
      selectedTemplate,
      savedProposal,
      savedProposalSignature: savedProposalSignatureRef.current,
      editProposalId,
      shareLinks,
      shareForm,
      partnerSettings,
    };
  }, [details, filters, options, clients, items, itemCache, groups, summary, selected, inventoryView, centerTab, searchQuery, clientSort, pageSize, chargeEdits, proposalTemplates, arrangementPresets, arrangement, gst, selectedTemplate, savedProposal, editProposalId, shareLinks, shareForm, partnerSettings]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedTemplateInfo = useMemo(() => proposalTemplates.find((t) => t.id === selectedTemplate), [proposalTemplates, selectedTemplate]);
  const selectedItems = useMemo(() => {
    const byId = new Map([...Object.values(itemCache), ...items].map((item) => [item.id, item]));
    return selected.map((id) => byId.get(id)).filter(Boolean);
  }, [itemCache, items, selected]);
  const durationMonths = Number(summary?.duration_months || 1);
  const selectedCost = selectedItems.reduce((sum, item) => sum + itemProposalTotal(item, chargeEdits, durationMonths), 0);
  const gstPct = Number(gst.gst_percentage || 0);
  const selectedTaxable = gst.gst_mode === "inclusive" && gstPct > 0 ? selectedCost / (1 + gstPct / 100) : selectedCost;
  const selectedGstAmount = gst.gst_mode === "inclusive" ? selectedCost - selectedTaxable : selectedCost * gstPct / 100;
  const selectedFinalAmount = gst.gst_mode === "inclusive" ? selectedCost : selectedCost + selectedGstAmount;
  const budget = parseMoneyInput(details.budget);
  const budgetWords = amountToIndianWords(details.budget);
  const hasSelection = selected.length > 0;
  const hasShareData = shareLinks.length > 0 || Boolean(savedProposal);
  const showSharePanel = canProposalCustom && (hasSelection || hasShareData);
  const hasInventoryData = items.length > 0 || selected.length > 0;
  const hasProposalSummary = hasSelection && summary;
  const centerSourceItems = centerTab === "selected" ? selectedItems : items;
  const centerFilteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let list = !query
      ? centerSourceItems
      : centerSourceItems.filter((item) => [item.hoarding_id, item.name, item.area, item.city]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query)));
    list = [...list].sort((a, b) => {
      const diff = Number(a.monthly_price || 0) - Number(b.monthly_price || 0);
      return clientSort === "price_asc" ? diff : -diff;
    });
    return list;
  }, [centerSourceItems, searchQuery, clientSort]);
  const pageCount = Math.max(1, Math.ceil(centerFilteredItems.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const centerPageItems = useMemo(
    () => centerFilteredItems.slice((safePage - 1) * pageSize, safePage * pageSize),
    [centerFilteredItems, safePage, pageSize],
  );
  const matchingFilteredIds = useMemo(
    () => (centerTab === "matching" ? centerFilteredItems.map((item) => item.id) : []),
    [centerTab, centerFilteredItems],
  );
  const allMatchingSelected = matchingFilteredIds.length > 0 && matchingFilteredIds.every((id) => selectedSet.has(id));
  const someMatchingSelected = matchingFilteredIds.some((id) => selectedSet.has(id));
  const selectedMediaPageCount = Math.max(1, Math.ceil(selectedItems.length / selectedMediaPageSize));
  const safeSelectedMediaPage = Math.min(selectedMediaPage, selectedMediaPageCount);
  const selectedMediaPageItems = useMemo(
    () => selectedItems.slice((safeSelectedMediaPage - 1) * selectedMediaPageSize, safeSelectedMediaPage * selectedMediaPageSize),
    [selectedItems, safeSelectedMediaPage, selectedMediaPageSize],
  );
  const canUsePartnerInventory = !!partnerSettings?.enabled && !!partnerSettings?.use_partner_inventory_enabled;
  const activeInventoryScope = filters.inventory_scope === "partner" ? "partner" : "owned";
  const selectedMyHoardingCount = selected.filter((id) => !String(id).startsWith("partner:")).length;
  const selectedPartnerHoardingCount = selected.filter((id) => String(id).startsWith("partner:")).length;

  useEffect(() => {
    if (selectAllMatchingRef.current) {
      selectAllMatchingRef.current.indeterminate = someMatchingSelected && !allMatchingSelected;
    }
  }, [allMatchingSelected, someMatchingSelected]);

  useEffect(() => {
    setPage(1);
  }, [centerTab, searchQuery, clientSort, items]);

  useEffect(() => {
    setSelectedMediaPage((current) => Math.min(current, Math.max(1, Math.ceil(selectedItems.length / selectedMediaPageSize))));
  }, [selectedItems.length, selectedMediaPageSize]);

  function toggleChargeExpanded(id) {
    setExpandedCharges((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const availableAreas = useMemo(() => {
    if (asArray(filters.cities).length === 0) return asArray(options.areas);
    const byCity = options.areas_by_city || {};
    const areaSet = new Set();
    filters.cities.forEach((city) => asArray(byCity[city]).forEach((area) => areaSet.add(area)));
    return [...areaSet].sort((a, b) => a.localeCompare(b));
  }, [filters.cities, options]);

  useEffect(() => {
    setFilters((prev) => {
      if (asArray(prev.areas).length === 0) return prev;
      const allowed = new Set(availableAreas);
      const nextAreas = prev.areas.filter((area) => allowed.has(area));
      return nextAreas.length === prev.areas.length ? prev : { ...prev, areas: nextAreas };
    });
  }, [availableAreas]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedTemplate) {
      setTemplateMapping(null);
      setMappingError("");
      return;
    }
    async function loadTemplateMapping() {
      setMappingLoading(true);
      try {
        const { data } = await api.get(`/proposals/templates/${selectedTemplate}/mapping`, { skipGlobalLoader: true });
        if (cancelled) return;
        setTemplateMapping(data);
        setMappingError("");
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setTemplateMapping(null);
          setMappingError("");
          await analyzeTemplateById(selectedTemplate, { silent: true });
          return;
        }
        setMappingError(friendlyTemplateMappingError(formatApiError(err.response?.data?.detail) || "Could not load template mapping."));
      } finally {
        if (!cancelled) setMappingLoading(false);
      }
    }
    loadTemplateMapping();
    return () => {
      cancelled = true;
    };
  }, [selectedTemplate]);

  function resetFilters() {
    const sizeExempt = savedSizeExemptions().filter((type) => asArray(options.media_types).includes(type));
    setFilters({
      ...EMPTY_FILTERS,
      inventory_scope: activeInventoryScope,
      size_exempt_media_types: sizeExempt,
    });
    setItems([]);
    setItemCache({});
    setSummary(null);
    setGroups([]);
    setSelected([]);
    setChargeEdits({});
    setSavedProposal(null);
    setShareLinks([]);
  }

  function validateDetails() {
    if (!details.client_name.trim()) return "Client name is required.";
    if (!details.campaign_name.trim()) return "Campaign name is required.";
    if (!details.start_date || !details.end_date) return "Start and end dates are required.";
    if (new Date(details.end_date) <= new Date(details.start_date)) return "End date must be after start date.";
    if (!details.proposal_expires_at) return "Proposal expiry date and time is required.";
    if (new Date(details.proposal_expires_at) <= new Date()) return "Proposal expiry must be in the future.";
    return "";
  }

  async function preview(nextFilters = filters, scroll = true) {
    if (!canProposalCustom && arrangement.type !== "default") {
      toast.error("Custom proposal arrangements are available in Premium and Enterprise plans.");
      return;
    }
    if ((arrangement.type === "custom_preset" || arrangement.type === "custom_flow") && !arrangement.preset_id) {
      toast.error("Choose an arrangement preset first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/proposals/preview", toPayload(details, nextFilters, [], {}, "", gst, arrangementPayload(arrangement, selected)));
      const nextItems = asArray(data?.items);
      setItems(nextItems);
      setItemCache((prev) => {
        const next = { ...prev };
        nextItems.forEach((item) => {
          next[item.id] = item;
        });
        return next;
      });
      setGroups(asArray(data?.groups));
      setSummary(data?.summary || null);
      setSavedProposal(null);
      setShareLinks([]);
      setInventoryView("list");
      if (nextItems.length === 0) toast.info("No inventory matched these filters.");
      if (scroll) requestAnimationFrame(() => inventoryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleSelected(id) {
    const item = itemCache[id] || items.find((entry) => entry.id === id);
    if (!selected.includes(id) && isBlockedInventory(item)) {
      toast.error("Blocked hoardings cannot be added to proposals");
      return;
    }
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function toggleAllMatching(checked) {
    if (checked) {
      setSelected((prev) => {
        const next = new Set(prev);
        matchingFilteredIds.forEach((id) => {
          const item = itemCache[id] || items.find((entry) => entry.id === id);
          if (!isBlockedInventory(item)) next.add(id);
        });
        return [...next];
      });
      return;
    }
    const matchingIds = new Set(matchingFilteredIds);
    setSelected((prev) => prev.filter((id) => !matchingIds.has(id)));
  }

  function switchInventoryScope(scope) {
    const nextFilters = { ...filters, inventory_scope: scope };
    setFilters(nextFilters);
    setCenterTab("matching");
    setPage(1);
    setSearchQuery("");
    if (hasInventoryData) preview(nextFilters, false);
  }

  function moveSelectedTo(id, targetIndex) {
    setSelected((prev) => {
      const currentIndex = prev.indexOf(id);
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= prev.length || currentIndex === targetIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setSavedProposal(null);
  }

  function toggleType(type) {
    setFilters((prev) => {
      const current = asArray(prev.media_types);
      return {
        ...prev,
        media_types: current.includes(type) ? current.filter((x) => x !== type) : [...current, type],
      };
    });
  }

  function toggleSizeExemptType(type) {
    setFilters((prev) => {
      const nextValues = toggleArray(prev.size_exempt_media_types, type);
      saveSizeExemptions(nextValues);
      return { ...prev, size_exempt_media_types: nextValues };
    });
  }

  function currentProposalPayload() {
    return toPayload(details, filters, selected, chargeEdits, selectedTemplate, gst, arrangementPayload(arrangement, selected));
  }

  function currentProposalSignature() {
    return JSON.stringify(currentProposalPayload());
  }

  function resetCreateProposalState() {
    const sizeExempt = savedSizeExemptions().filter((type) => asArray(options.media_types).includes(type));
    setDetails({ ...EMPTY_DETAILS });
    setFilters({
      ...EMPTY_FILTERS,
      inventory_scope: activeInventoryScope,
      size_exempt_media_types: sizeExempt,
    });
    setItems([]);
    setItemCache({});
    setGroups([]);
    setSummary(null);
    setSelected([]);
    setInventoryView("list");
    setCenterTab("matching");
    setSearchQuery("");
    setClientSort("price_desc");
    setPage(1);
    setPageSize(12);
    setSelectedMediaPage(1);
    setSelectedMediaPageSize(10);
    setExpandedCharges(new Set());
    setChargeEdits({});
    setArrangement({ type: "default", preset_id: "", manual_order: [] });
    setSelectedTemplate("");
    setTemplateName("");
    setTemplateFile(null);
    setTemplateMapping(null);
    setMappingError("");
    setShowTemplateMappingEditor(false);
    setBulkDisplay("");
    setBulkMounting("");
    setBulkPrinting("");
    setSavedProposal(null);
    setEditProposalId("");
    savedProposalSignatureRef.current = "";
    setShareLinks([]);
    setShareForm({
      expires_at: expiryFromDays(7),
      allow_pdf_download: true,
      allow_excel_download: false,
      allow_ppt_download: false,
      show_prices: true,
      allow_client_response: true,
      password: "",
      internal_note: "",
    });
    setFilePreview((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
    createProposalDraft = null;
  }

  async function generate(kind) {
    const err = validateDetails();
    if (err) {
      toast.error(err);
      return;
    }
    if (selected.length === 0) {
      toast.error("Select at least one media asset.");
      return;
    }
    setGenerating(kind);
    try {
      const proposal = await ensureCurrentProposalSaved();
      if (!proposal) return;
      const response = await api.get(`/proposals/${proposal.id}/download/${kind}`, { responseType: "blob" });
      const ext = kind === "ppt" ? "pptx" : kind;
      downloadBlob(response, `Hoardexa_Proposal.${ext}`);
      globalThis.__hoardexaSentProposalsCache = null;
      toast.success(`${kind.toUpperCase()} proposal saved and downloaded`);
    } catch (err) {
      const message = await responseBlobError(err);
      toast.error(message || "Could not generate proposal. Please try again.");
    } finally {
      setGenerating("");
    }
  }

  async function previewGeneratedFile(kind) {
    const err = validateDetails();
    if (err) {
      toast.error(err);
      return;
    }
    if (selected.length === 0) {
      toast.error("Select at least one media asset.");
      return;
    }
    setGenerating(`preview-${kind}`);
    try {
      const endpointKind = kind === "ppt" ? "ppt-preview" : kind;
      const response = await api.post(`/proposals/generate/${endpointKind}`, toPayload(details, filters, selected, chargeEdits, kind === "ppt" ? selectedTemplate : "", gst, arrangementPayload(arrangement, selected)), { responseType: "blob" });
      const ext = kind === "ppt" ? "pdf" : kind;
      const blob = blobFromResponse(response);
      const filename = filenameFromResponse(response, `Hoardexa_Proposal.${ext}`);
      const url = window.URL.createObjectURL(blob);
      setFilePreview((prev) => {
        if (prev?.url) window.URL.revokeObjectURL(prev.url);
        return {
          kind,
          blob,
          filename,
          url,
          type: response.headers?.["content-type"] || blob.type || "application/octet-stream",
        };
      });
    } catch (err) {
      const message = await responseBlobError(err);
      toast.error(message || "Could not prepare preview. Please try again.");
    } finally {
      setGenerating("");
    }
  }

  function closeFilePreview(open) {
    if (open) return;
    setFilePreview((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }

  async function saveCurrentProposal() {
    const err = validateDetails();
    if (err) {
      toast.error(err);
      return null;
    }
    if (selected.length === 0) {
      toast.error("Select at least one media asset.");
      return null;
    }
    const signature = currentProposalSignature();
    const payload = currentProposalPayload();
    const { data } = editProposalId
      ? await api.put(`/proposals/${editProposalId}`, payload)
      : await api.post("/proposals", payload);
    setSavedProposal(data);
    savedProposalSignatureRef.current = signature;
    globalThis.__hoardexaSentProposalsCache = null;
    return data;
  }

  async function ensureCurrentProposalSaved() {
    const signature = currentProposalSignature();
    if (savedProposal && savedProposalSignatureRef.current === signature) return savedProposal;
    return saveCurrentProposal();
  }

  async function saveAndGoToSent() {
    setGenerating("save");
    try {
      const proposal = await ensureCurrentProposalSaved();
      if (!proposal) return;
      resetCreateProposalState();
      delete globalThis.__hoardexaSentProposalsCache;
      toast.success("Proposal saved");
      navigate("/proposals/sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not save proposal");
    } finally {
      setGenerating("");
    }
  }

  function updateCharge(id, key, value) {
    const cleaned = String(value || "").replace(/[^\d.]/g, "");
    setChargeEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: cleaned },
    }));
    setSavedProposal(null);
  }

  function applyBulkCharges() {
    if (selected.length === 0) {
      toast.error("Select hoardings before bulk applying charges.");
      return;
    }
    setChargeEdits((prev) => {
      const next = { ...prev };
      selected.forEach((id) => {
        next[id] = { ...(next[id] || {}) };
        if (bulkDisplay !== "") next[id].display = String(bulkDisplay).replace(/[^\d.]/g, "");
        if (bulkMounting !== "") next[id].mounting = String(bulkMounting).replace(/[^\d.]/g, "");
        if (bulkPrinting !== "") next[id].printing = String(bulkPrinting).replace(/[^\d.]/g, "");
      });
      return next;
    });
    toast.success("Bulk charges applied");
  }

  async function uploadTemplate() {
    if (!templateName.trim()) {
      toast.error("Template name is required.");
      return;
    }
    if (!templateFile) {
      toast.error("Choose a PPTX file.");
      return;
    }
    const form = new FormData();
    form.append("name", templateName.trim());
    form.append("file", templateFile);
    setUploadingTemplate(true);
    try {
      const { data } = await api.post("/proposals/templates", form);
      setProposalTemplates((prev) => [data, ...prev]);
      setSelectedTemplate(data.id);
      setTemplateName("");
      setTemplateFile(null);
      toast.success("Proposal template uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingTemplate(false);
    }
  }

  function requestTemplateDelete(template, event) {
    event?.preventDefault();
    event?.stopPropagation();
    setTemplateSelectOpen(false);
    setDeleteTemplateTarget(template);
    setDeleteTemplateName("");
  }

  async function deleteProposalTemplate() {
    if (!deleteTemplateTarget) return;
    if (deleteTemplateName.trim() !== deleteTemplateTarget.name) {
      toast.error("Enter the exact template name to delete.");
      return;
    }
    setDeletingTemplate(true);
    try {
      await api.delete(`/proposals/templates/${deleteTemplateTarget.id}`);
      const nextTemplates = proposalTemplates.filter((template) => template.id !== deleteTemplateTarget.id);
      proposalReferenceCache.templates = nextTemplates;
      setProposalTemplates(nextTemplates);
      if (selectedTemplate === deleteTemplateTarget.id) {
        setSelectedTemplate("");
        setTemplateMapping(null);
        setMappingError("");
        setShowTemplateMappingEditor(false);
      }
      toast.success("Proposal template deleted");
      setDeleteTemplateTarget(null);
      setDeleteTemplateName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingTemplate(false);
    }
  }

  async function analyzeTemplateById(templateId, { silent = false } = {}) {
    if (!templateId) {
      toast.error("Choose a PPTX template first.");
      return;
    }
    setMappingLoading(true);
    setMappingError("");
    try {
      const { data } = await api.post(`/proposals/templates/${templateId}/analyze`);
      setTemplateMapping(data);
      if (!silent) toast.success(data.status === "failed" ? "Template needs manual review" : "Template analyzed");
    } catch (err) {
      const message = friendlyTemplateMappingError(formatApiError(err.response?.data?.detail) || err.message);
      setMappingError(message);
      if (!silent) toast.error(message);
    } finally {
      setMappingLoading(false);
    }
  }

  async function analyzeTemplate() {
    return analyzeTemplateById(selectedTemplate);
  }

  async function confirmTemplateMapping() {
    if (!selectedTemplate || !templateMapping?.mapping_json) return;
    setMappingSaving(true);
    try {
      const { data } = await api.put(`/proposals/templates/${selectedTemplate}/mapping`, { mapping_json: templateMapping.mapping_json });
      setTemplateMapping(data);
      setShowTemplateMappingEditor(false);
      toast.success("Template mapping confirmed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMappingSaving(false);
    }
  }

  async function resetTemplateMapping() {
    if (!selectedTemplate) return;
    setMappingSaving(true);
    try {
      await api.post(`/proposals/templates/${selectedTemplate}/mapping/reset`);
      setTemplateMapping(null);
      toast.success("Template mapping reset");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMappingSaving(false);
    }
  }

  function updateTemplateMapping(updater) {
    setTemplateMapping((prev) => {
      if (!prev?.mapping_json) return prev;
      const next = cloneJSON(prev.mapping_json);
      updater(next);
      return { ...prev, mapping_json: next };
    });
  }

  async function loadShareLinks(proposalId) {
    if (!proposalId) return;
    const { data } = await api.get(`/proposals/${proposalId}/share-links`, { skipGlobalLoader: true });
    setShareLinks(asArray(data));
  }

  async function createShareLink() {
    setSharing(true);
    try {
      const proposal = await ensureCurrentProposalSaved();
      if (!proposal) return;
      await api.post(`/proposals/${proposal.id}/share-link`, shareForm);
      resetCreateProposalState();
      delete globalThis.__hoardexaSentProposalsCache;
      toast.success("Public proposal link generated");
      navigate("/proposals/sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSharing(false);
    }
  }

  async function revokeLink(linkId) {
    try {
      const { data } = await api.post(`/proposal-share-links/${linkId}/revoke`);
      setShareLinks((prev) => prev.map((link) => link.id === linkId ? data : link));
      toast.success("Link revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function regenerateLink(linkId) {
    try {
      const { data } = await api.post(`/proposal-share-links/${linkId}/regenerate`);
      setShareLinks((prev) => prev.map((link) => link.id === linkId ? data : link));
      toast.success("New link generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  function copyLink(url) {
    navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  }

  const pieData = selectedSummaryRows(selectedItems, chargeEdits, durationMonths, "type", "Other").map((row) => ({ name: row.key, value: row.cost, count: row.count }));
  const cityRows = selectedSummaryRows(selectedItems, chargeEdits, durationMonths, "city", "Unknown City");
  const cityMax = Math.max(1, ...cityRows.map((r) => Number(r.cost || 0)));
  const sizeRows = selectedSummaryRows(selectedItems, chargeEdits, durationMonths, "size", "Unknown Size").slice(0, 5);
  const sizeMax = Math.max(1, ...sizeRows.map((r) => Number(r.cost || 0)));

  return (
    <Layout title="Create Proposal" subtitle="Build client-ready inventory proposals" mainClassName="bg-[#F8FAFC] p-3 sm:p-6">
      <div className="hx-proposal-v2 mx-auto w-full max-w-[1600px]" style={{ fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <div className="mb-4 flex min-w-0 flex-wrap items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-3 shadow-[0_1px_3px_rgba(15,23,42,0.08)] sm:gap-5 sm:px-6" style={{ minHeight: 76 }}>
          <div className="flex items-center gap-2 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F5F9] text-[#2563EB]"><Building2 size={17} /></div>
            <div>
              <div className="text-[11px] font-semibold text-[#64748B]">Client</div>
              <div className="max-w-[160px] truncate text-sm font-bold text-[#0F172A]">{details.client_name || "Not set"}</div>
            </div>
          </div>
          <VDivider />
          <div className="flex items-center gap-2 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F5F9] text-[#2563EB]"><Sun size={17} /></div>
            <div>
              <div className="text-[11px] font-semibold text-[#64748B]">Campaign</div>
              <div className="max-w-[160px] truncate text-sm font-bold text-[#0F172A]">{details.campaign_name || "Not set"}</div>
            </div>
          </div>
          <VDivider />
          <div className="flex items-center gap-5 py-3">
            <KpiBlock label="Matching Media" value={items.length} />
            <KpiBlock label="Selected Media" value={selected.length} />
            <KpiBlock label="Selected Cost" value={inr(selectedCost)} />
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 py-3">
            <Button type="button" variant="outline" onClick={resetCreateProposalState} disabled={generating !== ""} className="h-10 rounded-lg border-[#CBD5E1] px-4 text-[#334155]">
              <RotateCcw size={15} /> Reset All
            </Button>
            <Button type="button" variant="outline" onClick={() => previewGeneratedFile("ppt")} disabled={generating !== "" || selected.length === 0} className="h-10 rounded-lg border-[#CBD5E1] px-4 text-[#334155]">Preview PPT</Button>
            {canProposalPdf && <Button type="button" variant="outline" onClick={() => previewGeneratedFile("pdf")} disabled={generating !== "" || selected.length === 0} className="h-10 rounded-lg border-[#CBD5E1] px-4 text-[#334155]">Preview PDF</Button>}
            <div className="flex">
              <Button
                type="button"
                onClick={() => generate("ppt")}
                disabled={generating !== ""}
                className="h-[42px] rounded-r-none rounded-l-lg bg-[#2563EB] px-4 font-semibold text-white hover:bg-[#1D4ED8]"
              >
                <Presentation size={16} /> {generating ? "Generating..." : "Generate Proposal"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" disabled={generating !== ""} className="h-[42px] rounded-l-none rounded-r-lg border-l border-white/25 bg-[#2563EB] px-2 text-white hover:bg-[#1D4ED8]">
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canProposalPdf && <DropdownMenuItem onClick={() => generate("pdf")}><FileText size={14} /> Generate PDF</DropdownMenuItem>}
                  {canProposalExcel && <DropdownMenuItem onClick={() => generate("excel")}><FileSpreadsheet size={14} /> Generate Excel</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => generate("ppt")}><Presentation size={14} /> Generate PowerPoint</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <ProposalTabs />
        </div>

        <div className="space-y-4">
          <section className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-sm xl:col-span-2">
              <div className="text-[14px] font-bold text-[#0F172A]">Proposal Details</div>
              <div className="mt-2.5 grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
                <div className="space-y-1.5">
                  <RequiredLabel>Client Name</RequiredLabel>
                  <ClientNamePicker
                    value={details.client_name}
                    clients={clients}
                    loading={clientSearchLoading}
                    onChange={(clientName) => setDetails({ ...details, client_id: null, client_name: clientName })}
                    onPick={(client) => setDetails({ ...details, client_id: client.id || null, client_name: client.company || "" })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Brand Name</Label>
                  <Input value={details.brand_name} onChange={(e) => setDetails({ ...details, brand_name: e.target.value })} placeholder="Brand" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <RequiredLabel>Campaign Name</RequiredLabel>
                  <Input value={details.campaign_name} onChange={(e) => setDetails({ ...details, campaign_name: e.target.value })} placeholder="Summer launch" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <RequiredLabel>Start Date</RequiredLabel>
                  <Input type="date" value={details.start_date} onChange={(e) => setDetails({ ...details, start_date: e.target.value })} className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <RequiredLabel>End Date</RequiredLabel>
                  <Input type="date" value={details.end_date} onChange={(e) => setDetails({ ...details, end_date: e.target.value })} className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <RequiredLabel>Proposal Expiry</RequiredLabel>
                  <Input type="datetime-local" value={dateTimeLocalValue(details.proposal_expires_at)} onChange={(e) => setDetails({ ...details, proposal_expires_at: e.target.value })} className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Budget</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={details.budget}
                    onChange={(e) => setDetails({ ...details, budget: formatIndianDigits(e.target.value) })}
                    placeholder="0"
                    className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"
                  />
                  {budget > 999 && (
                    <p className="truncate text-[11px] font-medium text-[#64748B]">
                      {inr(budget)} {budgetWords ? `(${budgetWords} rupees)` : ""}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5 md:col-span-2 xl:col-span-4 2xl:col-span-5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Objective / Notes</Label>
                  <Textarea rows={2} value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} className="min-h-[66px] max-h-40 resize-y overflow-auto text-[13px]" />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-sm xl:min-h-[214px]">
              <div className="text-[14px] font-bold text-[#0F172A]">Tax &amp; Arrangement</div>
              <div className="mt-2.5 grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">GST Mode</Label>
                  <Select value={gst.gst_mode} onValueChange={(v) => setGst({ ...gst, gst_mode: v })}>
                    <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="exclusive">Exclusive</SelectItem><SelectItem value="inclusive">Inclusive</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">GST %</Label>
                  <Input type="number" min="0" value={gst.gst_percentage} onChange={(e) => setGst({ ...gst, gst_percentage: Number(e.target.value || 0) })} className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">GST Split</Label>
                  <Select value={gst.gst_treatment} onValueChange={(v) => setGst({ ...gst, gst_treatment: v })}>
                    <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cgst_sgst">CGST + SGST</SelectItem><SelectItem value="igst">IGST</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Round Off</Label>
                  <Select value={gst.round_off_behavior} onValueChange={(v) => setGst({ ...gst, round_off_behavior: v })}>
                    <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="nearest_rupee">Nearest rupee</SelectItem><SelectItem value="lower_rupee">Nearest lower rupee</SelectItem></SelectContent>
                  </Select>
                </div>
                {canProposalCustom && <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Arrange By</Label>
                  <Select value={arrangement.type} onValueChange={(v) => setArrangement((p) => ({ ...p, type: v, preset_id: v === "custom_preset" ? p.preset_id : "" }))}>
                    <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="city">City-wise</SelectItem>
                      <SelectItem value="area">Area-wise</SelectItem>
                      <SelectItem value="route_segment">Location type-wise</SelectItem>
                      <SelectItem value="direction">Traffic direction-wise</SelectItem>
                      <SelectItem value="custom_preset">Arrangement preset</SelectItem>
                    </SelectContent>
                  </Select>
                </div>}
                {canProposalCustom && arrangement.type === "custom_preset" && (
                  <div className="space-y-1.5">
                    <Label className="text-[12px] font-semibold text-[#334155]">Preset</Label>
                    <Select value={arrangement.preset_id || "none"} onValueChange={(v) => setArrangement((p) => ({ ...p, preset_id: v === "none" ? "" : v }))}>
                      <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Choose preset</SelectItem>
                        {arrangementPresets.map((preset) => (
                          <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {canProposalCustom && <div className="rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-sm xl:min-h-[214px]">
              <div className="text-[14px] font-bold text-[#0F172A]">PPT Template</div>
              <div className="mt-2.5 space-y-2.5">
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Use Template</Label>
                  <Select open={templateSelectOpen} onOpenChange={setTemplateSelectOpen} value={selectedTemplate || "default"} onValueChange={(v) => setSelectedTemplate(v === "default" ? "" : v)}>
                    <SelectTrigger className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default Hoardexa proposal</SelectItem>
                      {proposalTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id} className="pr-8">
                          <span className="flex w-full min-w-0 items-center justify-between gap-2">
                            <span className="truncate">{template.name}</span>
                            <span
                              role="button"
                              tabIndex={-1}
                              aria-label={`Delete ${template.name}`}
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50"
                              onPointerDown={(event) => requestTemplateDelete(template, event)}
                              onClick={(event) => requestTemplateDelete(template, event)}
                            >
                              <Trash2 size={14} />
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                  <Input type="file" accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation" onChange={(e) => setTemplateFile(e.target.files?.[0] || null)} className="text-[12px]" />
                  <Button type="button" variant="outline" onClick={uploadTemplate} disabled={uploadingTemplate} className="w-full">
                    <Upload size={15} /> {uploadingTemplate ? "Uploading..." : "Upload template"}
                  </Button>
                </div>
                {selectedTemplate && (
                  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 shadow-sm">
                    <div className="text-sm font-semibold text-[#0F172A]">{selectedTemplateInfo?.name || "Selected template"}</div>
                    <div className="text-xs text-[#64748B]">PowerPoint will be generated using this uploaded format.</div>
                    <span className="mt-2 inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#2563EB] ring-1 ring-[#BFDBFE]">
                      {mappingLoading ? "Preparing format..." : templateMapping?.status === "confirmed" ? "Format ready" : "Mapping review required"}
                    </span>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowTemplateMappingEditor((v) => !v)}>
                        {showTemplateMappingEditor ? "Hide template review" : "Review template"}
                      </Button>
                      {showTemplateMappingEditor && (
                        <Button type="button" variant="outline" onClick={analyzeTemplate} disabled={mappingLoading || mappingSaving}>
                          <RefreshCw size={14} /> {mappingLoading ? "Analyzing..." : "Re-detect format"}
                        </Button>
                      )}
                      {showTemplateMappingEditor && templateMapping?.mapping_json && (
                        <Button type="button" className="bg-[#2563EB] hover:bg-[#1D4ED8]" onClick={confirmTemplateMapping} disabled={mappingSaving}>
                          {mappingSaving ? "Saving..." : "Save mapping"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>}

            {canProposalCustom && selectedTemplate && showTemplateMappingEditor && (
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm xl:col-span-2">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-[15px] font-bold text-[#0F172A]">Template Review</h3>
                    <p className="mt-1 text-[12px] text-[#64748B]">Preview the uploaded PPT and confirm which fields Hoardexa should fill.</p>
                  </div>
                  {mappingError && (
                    <div className="max-w-[520px] rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                      {mappingError}
                    </div>
                  )}
                </div>
                {templateMapping?.mapping_json ? (
                  <TemplateMappingReview templateId={selectedTemplate} mapping={templateMapping.mapping_json} onChange={updateTemplateMapping} />
                ) : (
                  <div className="rounded-lg border border-dashed border-[#BFDBFE] bg-[#F8FAFC] p-8 text-center text-sm text-slate-500">
                    Preparing the template preview and field mapping...
                  </div>
                )}
              </div>
            )}

            <div ref={filtersPanelRef} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm xl:col-span-2">
              <div className="mb-3 flex items-center gap-1.5 text-[15px] font-bold text-[#0F172A]">
                <Filter size={15} className="text-[#2563EB]" /> Inventory Filters
              </div>
              <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-4">
                <SearchableMultiSelect
                  label="Cities"
                  placeholder="Search cities"
                  values={filters.cities}
                  options={options.cities}
                  emptyText="No cities found"
                  maxVisibleChips={1}
                  onChange={(cities) => setFilters((p) => ({ ...p, cities }))}
                />
                <SearchableMultiSelect
                  label="Areas"
                  placeholder={filters.cities.length ? "Search selected city areas" : "Search all areas"}
                  values={filters.areas}
                  options={availableAreas}
                  emptyText={filters.cities.length ? "No areas in selected cities" : "No areas found"}
                  onChange={(areas) => setFilters((p) => ({ ...p, areas }))}
                />
                <MultiSelect label="Media Types" values={filters.media_types} options={options.media_types} onToggle={toggleType} />
                <SearchableMultiSelect
                  label="Sizes"
                  placeholder="Search or pick sizes"
                  values={filters.sizes}
                  options={options.sizes}
                  emptyText="No sizes found"
                  onChange={(sizes) => setFilters((p) => ({ ...p, sizes, size: "" }))}
                />
                {asArray(options.media_types).length > 0 && (
                  <div className="space-y-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2.5">
                    <Label className="text-[11px] font-semibold text-[#334155]">Do not apply size filter to</Label>
                    <div className="flex flex-wrap gap-3">
                      {asArray(options.media_types).map((type) => (
                        <label key={type} className="flex items-center gap-1.5 text-[11px] font-medium text-[#334155]">
                          <input
                            type="checkbox"
                            checked={filters.size_exempt_media_types.includes(type)}
                            onChange={() => toggleSizeExemptType(type)}
                          />
                          {type}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <FilterSelect label="Illumination" value={filters.illumination} options={[["yes", "Illuminated"], ["no", "Non-illuminated"]]} onChange={(v) => setFilters({ ...filters, illumination: v })} />
                <div className="grid grid-cols-2 gap-2">
                  <NumberFilter label="Min Rate" value={filters.price_min} onChange={(v) => setFilters({ ...filters, price_min: v })} />
                  <NumberFilter label="Max Rate" value={filters.price_max} onChange={(v) => setFilters({ ...filters, price_max: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-[12px] font-semibold text-[#334155]">Allow intermittent availability</Label>
                  <Switch checked={filters.allow_intermittent} onCheckedChange={(v) => setFilters({ ...filters, allow_intermittent: v })} className="data-[state=checked]:bg-[#2563EB]" />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button type="button" onClick={resetFilters} className="h-10 flex-1 rounded-lg border border-[#2563EB] text-sm font-semibold text-[#2563EB] hover:bg-[#EFF6FF]">
                  Reset Filters
                </button>
                <button type="button" onClick={() => preview()} disabled={loading} className="h-10 flex-1 rounded-lg bg-[#2563EB] text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
                  {loading ? "Applying..." : "Apply Filters"}
                </button>
              </div>
            </div>
          </section>

          {(hasInventoryData || loading) && (
          <main className="min-w-0 space-y-4" ref={inventoryRef}>
            <div className="min-w-0 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-6 border-b border-[#E2E8F0] pb-[10px]">
                <button
                  type="button"
                  onClick={() => setCenterTab("matching")}
                  className={`text-[14px] font-bold pb-[10px] -mb-[11px] border-b-2 ${centerTab === "matching" ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-[#64748B]"}`}
                >
                  Matching Inventory{items.length > 0 ? ` (${items.length})` : ""}
                </button>
                {selected.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setCenterTab("selected")}
                    className={`text-[14px] font-bold pb-[10px] -mb-[11px] border-b-2 ${centerTab === "selected" ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-[#64748B]"}`}
                  >
                    Selected Inventory ({selected.length})
                  </button>
                )}
                <div className="ml-auto flex items-center gap-3">
                  {centerTab === "matching" && (
                    <label className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${matchingFilteredIds.length === 0 ? "cursor-not-allowed border-[#E2E8F0] text-[#94A3B8]" : "cursor-pointer border-[#CBD5E1] text-[#0F172A] hover:border-[#2563EB] hover:bg-[#EFF6FF]"}`}>
                      <input
                        ref={selectAllMatchingRef}
                        type="checkbox"
                        checked={allMatchingSelected}
                        disabled={matchingFilteredIds.length === 0}
                        onChange={(e) => toggleAllMatching(e.target.checked)}
                        className="h-4 w-4 accent-[#2563EB]"
                      />
                      Select all
                    </label>
                  )}
                  {selected.length > 0 && (
                    <button type="button" onClick={() => setSelected([])} className="text-xs font-semibold text-[#64748B] hover:text-[#EF4444]">Clear</button>
                  )}
                </div>
              </div>

              {centerTab === "matching" && canUsePartnerInventory && (
                <div className="mt-3 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => switchInventoryScope("owned")}
                    className={`rounded-md px-3 py-1.5 text-xs font-black transition ${
                      activeInventoryScope === "owned" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    My Hoardings
                    {selectedMyHoardingCount > 0 && <span className="ml-1 text-blue-600">{selectedMyHoardingCount}</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => switchInventoryScope("partner")}
                    className={`rounded-md px-3 py-1.5 text-xs font-black transition ${
                      activeInventoryScope === "partner" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Partner Network
                    {selectedPartnerHoardingCount > 0 && <span className="ml-1 text-blue-600">{selectedPartnerHoardingCount}</span>}
                  </button>
                </div>
              )}

              <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
                <div className="flex h-10 min-w-[220px] flex-1 items-center rounded-lg border border-[#CBD5E1] bg-white px-3">
                  <Search size={15} className="text-[#94A3B8]" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search inventory by code or location"
                    className="ml-2 min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-[#94A3B8]"
                  />
                </div>
                <button type="button" onClick={() => setInventoryView("list")} className={`flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${inventoryView === "list" ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-[#CBD5E1] bg-white text-[#334155]"}`}>
                  <List size={15} /> List
                </button>
                <button type="button" onClick={() => setInventoryView("tile")} className={`flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${inventoryView === "tile" ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-[#CBD5E1] bg-white text-[#334155]"}`}>
                  <Grid2X2 size={15} /> Grid
                </button>
                <Select value={clientSort} onValueChange={setClientSort}>
                  <SelectTrigger className="h-10 w-full min-w-[190px] sm:w-[230px] rounded-lg border-[#CBD5E1] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price_desc">Monthly Rate (High to Low)</SelectItem>
                    <SelectItem value="price_asc">Monthly Rate (Low to High)</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => filtersPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="flex h-10 items-center gap-1.5 rounded-lg border border-[#CBD5E1] bg-white px-3 text-xs font-semibold text-[#334155] hover:border-[#2563EB] hover:text-[#2563EB]"
                >
                  <SlidersHorizontal size={14} /> More Filters
                </button>
              </div>

              {centerTab === "matching" && items.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2.5 md:grid-cols-[repeat(3,minmax(0,1fr))_auto]">
                  <label className="space-y-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Display charges</span>
                    <Input value={bulkDisplay} onChange={(e) => setBulkDisplay(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Display charges" inputMode="decimal" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Mounting charges</span>
                    <Input value={bulkMounting} onChange={(e) => setBulkMounting(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Mounting charges" inputMode="decimal" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Printing charges</span>
                    <Input value={bulkPrinting} onChange={(e) => setBulkPrinting(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Printing charges" inputMode="decimal" className="h-9 rounded-lg border-[#CBD5E1] text-[13px]" />
                  </label>
                  <Button type="button" variant="outline" onClick={applyBulkCharges} disabled={selected.length === 0} className="h-9 self-end">Apply to selected</Button>
                </div>
              )}

              <div className="mt-4">
                {centerFilteredItems.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] py-16 text-center text-sm text-[#94A3B8]">
                    {centerTab === "selected" ? "Nothing selected yet. Add media from the Matching Inventory tab." : "No inventory matched yet. Apply filters to search."}
                  </div>
                ) : inventoryView === "list" ? (
                  <InventoryList
                    items={centerPageItems}
                    selectedSet={selectedSet}
                    chargeEdits={chargeEdits}
                    durationMonths={durationMonths}
                    onToggle={toggleSelected}
                    onCharge={updateCharge}
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {centerPageItems.map((item, index) => (
                      <InventoryCardV2
                        key={item.id}
                        item={item}
                        row={index}
                        active={selectedSet.has(item.id)}
                        chargeEdits={chargeEdits}
                        durationMonths={durationMonths}
                        onToggle={toggleSelected}
                        chargesOpen={expandedCharges.has(item.id)}
                        onToggleCharges={toggleChargeExpanded}
                        onCharge={updateCharge}
                      />
                    ))}
                  </div>
                )}
              </div>

              <Pagination page={safePage} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} total={centerFilteredItems.length} />
            </div>
          </main>
          )}

          {(hasSelection || showSharePanel) && (
          <section className="min-w-0 space-y-4">
            {hasSelection && (
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-bold text-[#0F172A]">Selected Media ({selected.length})</h3>
                {selected.length > 0 && <button type="button" onClick={() => setSelected([])} className="text-[12px] font-semibold text-[#2563EB] hover:underline">Clear All</button>}
              </div>
              <div className="mt-3 space-y-2">
                {selectedItems.length === 0 && <div className="text-xs text-[#94A3B8]">Nothing selected yet. Pick media in the center column to include them here.</div>}
                {selectedMediaPageItems.map((item, index) => (
                  <SelectedMediaRow
                    key={item.id}
                    item={item}
                    index={(safeSelectedMediaPage - 1) * selectedMediaPageSize + index}
                    onRemove={toggleSelected}
                    onMove={moveSelectedTo}
                  />
                ))}
              </div>
              <Pagination
                page={safeSelectedMediaPage}
                pageCount={selectedMediaPageCount}
                onPageChange={setSelectedMediaPage}
                pageSize={selectedMediaPageSize}
                onPageSizeChange={(value) => {
                  setSelectedMediaPageSize(value);
                  setSelectedMediaPage(1);
                }}
                total={selectedItems.length}
                pageSizeOptions={[10, 25, 50]}
              />
              {hasSelection && (
                <>
                  <div className="my-3 border-t border-[#E2E8F0]" />
                  <div className="space-y-1.5 text-[13px]">
                    <div className="flex items-center justify-between"><span className="text-[#64748B]">Selected Cost</span><span className="font-semibold text-[#0F172A]">{inr(selectedCost)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-[#64748B]">GST ({gstPct}%)</span><span className="font-semibold text-[#0F172A]">{inr(selectedGstAmount)}</span></div>
                    <div className="flex items-center justify-between text-[15px] font-extrabold"><span className="text-[#0F172A]">Proposal Final</span><span className="text-[#2563EB]">{inr(selectedFinalAmount)}</span></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => bottomSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="mt-3 h-10 w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] text-sm font-semibold text-[#2563EB]"
                  >
                    Proposal Summary
                  </button>
                </>
              )}
            </div>
            )}

            {showSharePanel && (
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
                <h3 className="text-[15px] font-bold text-[#0F172A]">Share Proposal</h3>
                <p className="text-[12px] text-[#64748B]">Create a public client link with expiry and download controls.</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[[1, "24 hours"], [3, "3 days"], [7, "7 days"], [15, "15 days"]].map(([days, label]) => {
                    const value = expiryFromDays(days);
                    const active = shareForm.expires_at === value;
                    return (
                      <label key={days} className={`flex h-9 items-center gap-2 rounded-lg border px-2.5 text-xs font-semibold ${active ? "border-[#2563EB] text-[#2563EB]" : "border-[#E2E8F0] text-[#334155]"}`}>
                        <input type="radio" name="share-expiry" checked={active} onChange={() => setShareForm({ ...shareForm, expires_at: value })} />
                        {label}
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Expires At</Label>
                  <div className="relative">
                    <CalendarDays size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    <Input type="datetime-local" value={shareForm.expires_at} onChange={(e) => setShareForm({ ...shareForm, expires_at: e.target.value })} className="h-[38px] rounded-lg border-[#CBD5E1] pl-9 text-[13px]" />
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Optional Password</Label>
                  <div className="relative">
                    <Input
                      type={showSharePassword ? "text" : "password"}
                      value={shareForm.password}
                      onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })}
                      placeholder="Leave blank for no password"
                      className="h-[38px] rounded-lg border-[#CBD5E1] pr-9 text-[13px]"
                    />
                    <button type="button" onClick={() => setShowSharePassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#2563EB]" aria-label="Toggle password visibility">
                      {showSharePassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] text-[#334155]">
                  {[
                    canProposalPdf && ["allow_pdf_download", "Allow PDF download"],
                    canProposalExcel && ["allow_excel_download", "Allow Excel download"],
                    ["allow_ppt_download", "Allow PPT download"],
                    ["show_prices", "Show prices"],
                    ["allow_client_response", "Allow client response"],
                  ].filter(Boolean).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input type="checkbox" checked={shareForm[key]} onChange={(e) => setShareForm({ ...shareForm, [key]: e.target.checked })} />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label className="text-[12px] font-semibold text-[#334155]">Internal Note</Label>
                  <Textarea rows={2} value={shareForm.internal_note} onChange={(e) => setShareForm({ ...shareForm, internal_note: e.target.value })} className="text-[13px]" />
                </div>
                <Button onClick={createShareLink} disabled={sharing || selected.length === 0} className="mt-3 h-10 w-full rounded-lg bg-[#2563EB] font-bold text-white hover:bg-[#1D4ED8]">
                  <Link2 size={16} /> {sharing ? "Creating..." : "Generate Share Link"}
                </Button>
                <div className="mt-3 space-y-2">
                  {shareLinks.map((link) => (
                    <div key={link.id} className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[11px] font-semibold uppercase text-[#64748B]">{link.status} | opens {link.open_count || 0}</div>
                          <div className="text-[11px] text-[#64748B]">Expires {formatDateTime(link.expires_at)}</div>
                          {link.last_opened_at && <div className="text-[11px] text-[#64748B]">Last opened {formatDateTime(link.last_opened_at)}</div>}
                        </div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => copyLink(link.share_url)} className="rounded-lg border border-[#E2E8F0] bg-white p-1.5 text-[#334155] hover:text-[#2563EB]" title="Copy link"><Copy size={13} /></button>
                          <button type="button" onClick={() => regenerateLink(link.id)} className="rounded-lg border border-[#E2E8F0] bg-white p-1.5 text-[#334155] hover:text-[#2563EB]" title="Regenerate link"><RefreshCw size={13} /></button>
                          <button type="button" onClick={() => revokeLink(link.id)} className="rounded-lg border border-red-200 bg-white p-1.5 text-[#EF4444] hover:bg-red-50" title="Revoke link"><XCircle size={13} /></button>
                        </div>
                      </div>
                      <div className="mt-1.5 truncate text-[11px] font-mono text-[#2563EB]">{link.share_url}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
          )}
        </div>

        {hasProposalSummary && (
        <div ref={bottomSummaryRef} className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm lg:grid-cols-[240px_1fr_1fr_1fr]">
          <div>
            <h3 className="text-[15px] font-bold text-[#0F172A]">Proposal Summary</h3>
            <p className="text-[12px] text-[#64748B]">Preview totals before generating client files.</p>
            <div className="mt-3 flex flex-col gap-2">
              <GenerateFileButton icon={Save} label="Save" color="#2563EB" onClick={saveAndGoToSent} disabled={generating !== "" || selected.length === 0} />
              <GenerateFileButton icon={FileSpreadsheet} label="Excel" color="#16A34A" onClick={() => generate("excel")} disabled={generating !== "" || selected.length === 0} />
              <GenerateFileButton icon={Presentation} label="PowerPoint" color="#EA580C" onClick={() => generate("ppt")} disabled={generating !== "" || selected.length === 0} />
              <GenerateFileButton icon={FileText} label="PDF" color="#DC2626" onClick={() => generate("pdf")} disabled={generating !== "" || selected.length === 0} />
            </div>
            {generating && <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#64748B]"><Download size={12} /> Generating {generating.toUpperCase()}...</div>}
          </div>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-[#64748B]">City-wise</div>
            <div className="mt-2 space-y-3">
              {cityRows.length === 0 && <div className="text-xs text-[#94A3B8]">No data yet</div>}
              {cityRows.map((row) => (
                <HorizontalBarRow key={row.key} label={row.key} count={row.count} cost={row.cost} maxCost={cityMax} />
              ))}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-[#64748B]">Media Type-wise</div>
            {pieData.length === 0 ? (
              <div className="mt-2 text-xs text-[#94A3B8]">No data yet</div>
            ) : (
              <div className="mt-1 flex items-center gap-3">
                <div className="h-[120px] w-[120px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={56} paddingAngle={2}>
                        {pieData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1 text-[11px]">
                  {pieData.map((row, i) => (
                    <div key={row.name} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 truncate font-semibold text-[#334155]">
                        <span className="h-2 w-2 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} /> {row.name}
                      </span>
                      <span className="whitespace-nowrap text-[#64748B]">{row.count} | {inr(row.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-[#64748B]">Size-wise Top 5</div>
            <div className="mt-2 space-y-2">
              {sizeRows.length === 0 && <div className="text-xs text-[#94A3B8]">No data yet</div>}
              {sizeRows.map((row) => (
                <MiniBarRow key={row.key} label={row.key} count={row.count} cost={row.cost} maxCost={sizeMax} />
              ))}
            </div>
          </div>
        </div>
        )}

        <Dialog open={Boolean(deleteTemplateTarget)} onOpenChange={(open) => { if (!open && !deletingTemplate) setDeleteTemplateTarget(null); }}>
          <DialogContent className="rounded-xl border-rose-100 p-0 sm:max-w-md">
            <DialogHeader className="border-b border-rose-100 bg-rose-50 px-5 py-4">
              <DialogTitle className="text-base font-bold text-rose-950">Delete template?</DialogTitle>
              <DialogDescription className="text-sm text-rose-700">
                Are you sure you want to delete this template? Type the exact name to confirm.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 px-5 py-4">
              <div className="rounded-lg border border-rose-100 bg-white px-3 py-2 text-sm font-semibold text-[#0F172A]">
                {deleteTemplateTarget?.name}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] font-semibold text-[#334155]">Template name</Label>
                <Input
                  value={deleteTemplateName}
                  onChange={(e) => setDeleteTemplateName(e.target.value)}
                  placeholder={deleteTemplateTarget?.name || "Template name"}
                  className="h-9 rounded-lg border-[#CBD5E1] text-[13px]"
                  autoFocus
                />
              </div>
            </div>}
            <DialogFooter className="gap-2 border-t border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
              <Button type="button" variant="outline" onClick={() => setDeleteTemplateTarget(null)} disabled={deletingTemplate}>Cancel</Button>
              <Button
                type="button"
                variant="destructive"
                onClick={deleteProposalTemplate}
                disabled={deletingTemplate || deleteTemplateName.trim() !== (deleteTemplateTarget?.name || "")}
              >
                <Trash2 size={14} /> {deletingTemplate ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(filePreview)} onOpenChange={closeFilePreview}>
          <DialogContent className="flex h-[92vh] w-[96vw] max-w-[1200px] flex-col overflow-hidden rounded-xl border-[#CBD5E1] p-0">
            <DialogHeader className="border-b border-[#E2E8F0] px-5 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
                <div>
                  <DialogTitle className="text-base font-bold text-[#0F172A]">
                    {filePreview?.kind === "ppt" ? "PowerPoint Preview" : "PDF Preview"}
                  </DialogTitle>
                  <DialogDescription className="mt-1 truncate text-xs text-[#64748B]">
                    {filePreview?.filename || "Hoardexa proposal"}
                  </DialogDescription>
                </div>
                {filePreview && (
                  <Button
                    type="button"
                    onClick={() => generate(filePreview.kind === "ppt" ? "ppt" : "pdf")}
                    disabled={generating !== ""}
                    className="h-9 rounded-lg bg-[#2563EB] px-3 text-xs font-bold text-white hover:bg-[#1D4ED8]"
                  >
                    <Download size={14} /> {filePreview.kind === "ppt" ? "Download PPT" : "Download PDF"}
                  </Button>
                )}
              </div>
            </DialogHeader>
            <div className="min-h-0 flex-1 bg-[#F8FAFC] p-3">
              {filePreview ? (
                <iframe
                  title={filePreview.kind === "ppt" ? "Proposal PowerPoint preview" : "Proposal PDF preview"}
                  src={filePreview.url}
                  className="h-full w-full rounded-lg border border-[#CBD5E1] bg-white"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function handleChargeKeyDown(e) {
  const row = Number(e.currentTarget.dataset.row || 0);
  const col = Number(e.currentTarget.dataset.col || 0);
  if (e.key === "ArrowLeft" && col === 0) {
    e.preventDefault();
    document.querySelector(`[data-checkbox-row="${row}"]`)?.focus();
    return;
  }
  let next;
  if (e.key === "ArrowRight") next = document.querySelector(`[data-charge-cell="${row}:${col + 1}"]`);
  if (e.key === "ArrowLeft") next = document.querySelector(`[data-charge-cell="${row}:${col - 1}"]`);
  if (e.key === "ArrowDown") next = document.querySelector(`[data-charge-cell="${row + 1}:${col}"]`);
  if (e.key === "ArrowUp") next = document.querySelector(`[data-charge-cell="${row - 1}:${col}"]`);
  if (e.key === "Enter") {
    e.currentTarget.blur();
    return;
  }
  if (next) {
    e.preventDefault();
    next.focus();
    next.select?.();
  }
}

function handleCheckboxKeyDown(e, row, itemId, onToggle) {
  if (e.key === "ArrowDown") {
    e.preventDefault();
    document.querySelector(`[data-checkbox-row="${row + 1}"]`)?.focus();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    document.querySelector(`[data-checkbox-row="${row - 1}"]`)?.focus();
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    document.querySelector(`[data-charge-cell="${row}:0"]`)?.focus();
  } else if (e.key === "Enter") {
    e.preventDefault();
    onToggle(itemId);
  }
}

function focusInventoryCheckbox(row) {
  requestAnimationFrame(() => {
    document.querySelector(`[data-checkbox-row="${row}"]`)?.focus();
  });
}

function ChargeInput({ item, chargeEdits, field, row, col, onCharge }) {
  const defaults = {
    display: item.default_display_charge ?? item.monthly_price ?? 0,
    mounting: item.default_mounting_charge ?? 0,
    printing: item.default_printing_charge ?? 0,
  };
  const custom = isCustomCharge(item, chargeEdits, field);
  const value = chargeEdits[item.id]?.[field] ?? "";
  const minMap = {
    display: item.min_display_charge,
    mounting: item.mounting_responsibility === "owner" ? item.min_mounting_charge : null,
    printing: item.printing_responsibility === "owner" ? item.min_printing_charge : null,
  };
  const minValue = Number(minMap[field] || 0);
  return (
    <div>
      <Input
        data-charge-cell={`${row}:${col}`}
        data-row={row}
        data-col={col}
        inputMode="decimal"
        value={value}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onChange={(e) => onCharge(item.id, field, e.target.value)}
        onKeyDown={handleChargeKeyDown}
        placeholder={String(defaults[field] || 0)}
        className={`h-8 w-full min-w-0 px-2 text-xs font-semibold text-[#0F172A] placeholder:text-[#0F172A]/55 ${custom ? "border-blue-300 bg-blue-50 text-blue-900" : ""}`}
        aria-label={`${field} charge for ${item.name}`}
      />
      {item.inventory_source === "partner" && minValue > 0 && (
        <div className="mt-1 truncate text-[10px] font-bold text-amber-700">Min {inr(minValue)}</div>
      )}
    </div>
  );
}

function availabilityStatus(item) {
  if (!item.requested_days) return null;
  const full = Boolean(item.fully_available);
  return { full, count: full ? item.requested_days : (item.availability_days || 0) };
}

function isBlockedInventory(item) {
  return item?.occupancy_status === "Blocked";
}

function availabilityToneClasses(avail) {
  if (!avail) return { bg: "", border: "", text: "" };
  return avail.full
    ? { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700" }
    : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" };
}

function AvailabilityBadge({ item }) {
  const avail = availabilityStatus(item);
  if (!avail) return <span className="text-xs text-slate-500">{item.occupancy_status}</span>;
  const tone = availabilityToneClasses(avail);
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-extrabold ${tone.text}`} title={avail.full ? "Fully available" : "Partially available"}>
      <CalendarDays size={13} /> {avail.count}
    </span>
  );
}

function InventoryCardV2({ item, row, active, chargeEdits, durationMonths, onToggle, chargesOpen, onToggleCharges, onCharge }) {
  const avail = availabilityStatus(item);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={() => {
        onToggle(item.id);
        focusInventoryCheckbox(row);
      }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(item.id);
          focusInventoryCheckbox(row);
        }
      }}
      className={`group cursor-pointer overflow-hidden rounded-xl border border-[#E2E8F0] ${active ? "bg-[#EFF6FF] text-[#0F172A]" : "bg-white"} shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition hover:border-[#93C5FD] hover:shadow-[0_8px_20px_rgba(15,23,42,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/35`}
    >
      <div className="relative h-40 w-full bg-[#F1F5F9]">
        {item.photo_url
          ? <img src={item.photo_url} alt="" className="h-full w-full object-cover" />
          : <div className="flex h-full w-full items-center justify-center text-[11px] font-medium text-[#94A3B8]">No photo</div>}
        <label className="absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-md bg-white/95 shadow">
          <input
            type="checkbox"
            data-checkbox-row={row}
            checked={active}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(item.id)}
            onKeyDown={(e) => handleCheckboxKeyDown(e, row, item.id, onToggle)}
            className="h-3.5 w-3.5"
          />
        </label>
        {avail && (
          <span className="absolute right-2 top-2 rounded-md bg-[#2563EB] px-2 py-1 text-[11px] font-semibold text-white">
            {avail.count} Day{avail.count === 1 ? "" : "s"}
          </span>
        )}
        {item.inventory_source === "partner" && (
          <span className="absolute bottom-2 left-2 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-black text-white">Partner</span>
        )}
      </div>
      <div className="p-3">
        <div className="text-[13px] font-extrabold text-[#0F172A]">{item.hoarding_id}</div>
        <div className="mt-0.5 line-clamp-2 text-[13px] font-bold leading-[18px] text-[#0F172A]">{item.name}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.inventory_source === "partner" && <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-black text-emerald-700">Partner</span>}
          <span className="rounded-md bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#334155]">{item.size}</span>
          <span className="rounded-md bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#334155]">{item.type}</span>
        </div>
        <div className="mt-2 flex items-center gap-1 text-[12px] font-semibold text-[#0F172A]">
          <MapPin size={12} /> {item.area ? `${item.area}, ${item.city}` : item.city}
        </div>
        <div className="mt-2 flex items-end justify-between">
          <div>
            <span className="text-[18px] font-extrabold text-[#0F172A]">{inr(chargeValue(item, chargeEdits, "display"))}</span>
            <span className="ml-1 text-[11px] text-[#64748B]">/month</span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCharges(item.id);
            }}
            className="text-[11px] font-semibold text-[#2563EB] hover:underline"
          >
            {chargesOpen ? "Hide charges" : "Edit charges"}
          </button>
        </div>
        {chargesOpen && (
          <div className="mt-2 grid grid-cols-[1.35fr_1fr_1fr] gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2">
            <ChargeInput item={item} chargeEdits={chargeEdits} field="display" row={row} col={0} onCharge={onCharge} />
            <ChargeInput item={item} chargeEdits={chargeEdits} field="mounting" row={row} col={1} onCharge={onCharge} />
            <ChargeInput item={item} chargeEdits={chargeEdits} field="printing" row={row} col={2} onCharge={onCharge} />
            <div className="col-span-3 flex items-center justify-between text-xs">
              <span className="font-medium text-[#64748B]">Total</span>
              <span className="font-semibold text-[#0F172A]">{inr(itemProposalTotal(item, chargeEdits, durationMonths))}</span>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item.id);
            focusInventoryCheckbox(row);
          }}
          className={`mt-3 h-[38px] w-full rounded-lg border text-sm font-semibold transition-colors ${active ? "border-[#EF4444] bg-white text-[#EF4444] hover:bg-red-50" : "border-[#2563EB] bg-white text-[#2563EB] hover:bg-[#EFF6FF]"}`}
        >
          {active ? "− Remove from Proposal" : "+ Add to Proposal"}
        </button>
      </div>
    </div>
  );
}

function InventoryList({ items, selectedSet, chargeEdits, durationMonths, onToggle, onCharge }) {
  return (
    <div className="hx-proposal-inventory-list">
      <div className="hx-proposal-inventory-head">
        <span>Select</span>
        <span>Hoarding</span>
        <span>Size</span>
        <span>Media</span>
        <span>Availability</span>
        <span>Display</span>
        <span>Mounting</span>
        <span>Printing</span>
        <span className="text-right">Total</span>
      </div>
      <div className="hx-proposal-inventory-body">
        {items.map((item, index) => (
          <InventoryRow
            key={item.id}
            item={item}
            index={index}
            active={selectedSet.has(item.id)}
            chargeEdits={chargeEdits}
            durationMonths={durationMonths}
            onToggle={onToggle}
            onCharge={onCharge}
          />
        ))}
      </div>
    </div>
  );
}

function InventoryRow({ item, index, active, chargeEdits, durationMonths, onToggle, onCharge }) {
  const avail = availabilityStatus(item);
  const tone = availabilityToneClasses(avail);
  const rowBg = active ? "bg-[#EFF6FF]" : tone.bg || "bg-white";
  const labelClass = "hx-proposal-cell-label text-[10px] font-semibold uppercase text-black";
  const valueClass = "text-xs font-bold text-[#0F172A]";
  return (
      <div
        role="button"
        tabIndex={0}
        aria-pressed={active}
        onClick={() => {
          onToggle(item.id);
          focusInventoryCheckbox(index);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(item.id);
            focusInventoryCheckbox(index);
          }
        }}
        className={`hx-proposal-inventory-row cursor-pointer ${rowBg} text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/35`}
      >
        <div className="hx-proposal-cell hx-select-cell">
          <input
            type="checkbox"
            data-checkbox-row={index}
            checked={active}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(item.id)}
            onKeyDown={(e) => handleCheckboxKeyDown(e, index, item.id, onToggle)}
          />
        </div>
        <div className="hx-proposal-cell hx-hoarding-cell">
          <div className="hx-proposal-hoarding-identity flex items-center gap-2">
            <span className={`font-mono ${valueClass}`}>{item.hoarding_id}</span>
            {item.inventory_source === "partner" && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-black uppercase text-emerald-700">Partner</span>}
            <span className={`min-w-0 truncate ${valueClass}`}>{item.name}</span>
          </div>
        </div>
        <div className="hx-meta-group">
          <div className="hx-proposal-cell hx-meta-cell">
            <span className={labelClass}>Size</span><span className={valueClass}>{item.size}</span>
          </div>
          <div className="hx-proposal-cell hx-meta-cell">
            <span className={labelClass}>Media</span><span className={valueClass}>{item.type}</span>
          </div>
          <div className="hx-proposal-cell hx-meta-cell">
            <AvailabilityBadge item={item} />
          </div>
        </div>
        <div className="hx-charge-group">
          <div className="hx-proposal-cell hx-charge-cell">
            <span className={labelClass}>Display charges</span>
            <ChargeInput item={item} chargeEdits={chargeEdits} field="display" row={index} col={0} onCharge={onCharge} />
          </div>
          <div className="hx-proposal-cell hx-charge-cell">
            <span className={labelClass}>Mounting charges</span>
            <ChargeInput item={item} chargeEdits={chargeEdits} field="mounting" row={index} col={1} onCharge={onCharge} />
          </div>
          <div className="hx-proposal-cell hx-charge-cell">
            <span className={labelClass}>Printing charges</span>
            <ChargeInput item={item} chargeEdits={chargeEdits} field="printing" row={index} col={2} onCharge={onCharge} />
          </div>
        </div>
        <div className="hx-proposal-cell hx-total-cell">
          <span className={labelClass}>Total:</span>
          <span className={valueClass}>{inr(itemProposalTotal(item, chargeEdits, durationMonths))}</span>
        </div>
      </div>
  );
}

function expiryFromDays(days) {
  const d = new Date(Date.now() + days * 86400000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function FilterSelect({ label, value, options = [], onChange }) {
  const normalized = asArray(options).map((option) => Array.isArray(option) ? option : [option, option]);
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] font-semibold text-[#334155]">{label}</Label>
      <Select value={value || "any"} onValueChange={(v) => onChange(v === "any" ? "" : v)}>
        <SelectTrigger className="h-[38px] rounded-lg border-[#CBD5E1] text-[13px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any</SelectItem>
          {normalized.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberFilter({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] font-semibold text-[#334155]">{label}</Label>
      <Input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="h-[38px] rounded-lg border-[#CBD5E1] text-[13px] placeholder:text-[#94A3B8]" />
    </div>
  );
}

function SearchableMultiSelect({ label, values = [], options = [], placeholder, emptyText = "No results", onChange, maxVisibleChips = Infinity }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);
  const selected = asArray(values);
  const normalized = asArray(options).filter(Boolean);
  const filtered = normalized
    .filter((option) => !selected.includes(option))
    .filter((option) => String(option).toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 80);
  const visibleChips = showAllChips ? selected : selected.slice(0, maxVisibleChips);
  const hiddenCount = selected.length - visibleChips.length;

  function addValue(value) {
    if (!value || selected.includes(value)) return;
    onChange([...selected, value]);
    setQuery("");
    setOpen(false);
  }

  function removeValue(value) {
    onChange(selected.filter((item) => item !== value));
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] font-semibold text-[#334155]">{label}</Label>
      <div className="relative">
        <div className="flex h-[38px] items-center rounded-lg border border-[#CBD5E1] bg-white shadow-sm focus-within:ring-1 focus-within:ring-[#2563EB]">
          <Search size={15} className="ml-3 text-[#94A3B8]" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent px-2 text-[13px] outline-none placeholder:text-[#94A3B8]"
          />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((v) => !v)} className="flex h-[38px] w-9 items-center justify-center text-[#94A3B8] hover:text-[#2563EB]" aria-label={`Open ${label}`}>
            <ChevronDown size={16} />
          </button>
        </div>
        {open && (
          <div className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-[#E2E8F0] bg-white p-1 shadow-lg">
            {filtered.map((option) => (
              <button
                key={option}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addValue(option)}
                className="block w-full rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-[#EFF6FF] hover:text-[#2563EB]"
              >
                {option}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-400">{emptyText}</div>}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visibleChips.map((value) => (
            <span key={value} className="inline-flex max-w-full items-center gap-1 rounded-md border border-[#E2E8F0] bg-[#F1F5F9] px-2 py-1 text-xs font-medium text-[#334155]">
              <span className="truncate">{value}</span>
              <button type="button" onClick={() => removeValue(value)} className="text-[#64748B] hover:text-[#2563EB]" aria-label={`Remove ${value}`}>
                <X size={12} />
              </button>
            </span>
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllChips(true)}
              className="rounded-md border border-[#E2E8F0] bg-[#F1F5F9] px-2 py-1 text-xs font-semibold text-[#334155] hover:text-[#2563EB]"
            >
              +{hiddenCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MultiSelect({ label, values = [], options = [], onToggle }) {
  const list = asArray(options);
  const summary = list.length === 0 ? "" : values.length === list.length ? `All selected (${list.length})` : `${values.length} selected`;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[12px] font-semibold text-[#334155]">{label}</Label>
        {summary && <span className="text-[11px] font-medium text-[#64748B]">{summary}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((option) => {
          const active = values.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${active ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-[#CBD5E1] bg-white text-[#334155] hover:border-[#2563EB] hover:text-[#2563EB]"}`}
            >
              {option}
            </button>
          );
        })}
        {list.length === 0 && <span className="text-xs text-slate-400">No options yet</span>}
      </div>
    </div>
  );
}

function KpiBlock({ label, value }) {
  return (
    <div>
      <div className="text-[20px] font-bold tabular-nums leading-tight text-[#2563EB]">{value}</div>
      <div className="text-[12px] text-[#64748B]">{label}</div>
    </div>
  );
}

function VDivider() {
  return <div className="hidden h-9 w-px shrink-0 bg-[#E2E8F0] sm:block" />;
}

function Pagination({ page, pageCount, onPageChange, pageSize, onPageSizeChange, total, pageSizeOptions = [12, 24, 48] }) {
  if (total === 0) return null;
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1).slice(0, 7);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange(p)}
          className={`h-8 w-8 rounded-lg text-xs font-semibold ${p === page ? "bg-[#2563EB] text-white" : "border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#2563EB]"}`}
        >
          {p}
        </button>
      ))}
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger className="ml-2 h-8 w-[120px] rounded-lg border-[#CBD5E1] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map((option) => (
            <SelectItem key={option} value={String(option)}>{option} per page</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SelectedMediaRow({ item, index, onRemove, onMove }) {
  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", item.id);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDrop={(event) => {
        event.preventDefault();
        const draggedId = event.dataTransfer.getData("text/plain");
        if (draggedId) onMove(draggedId, index);
      }}
      className="flex h-[74px] items-center gap-2 rounded-[10px] border border-[#E2E8F0] bg-white px-2 transition hover:border-[#93C5FD] hover:bg-[#F8FAFC]"
    >
      <GripVertical size={14} className="shrink-0 cursor-grab text-[#94A3B8] active:cursor-grabbing" />
      <span className="w-5 shrink-0 text-center text-xs font-mono text-[#94A3B8]">{index + 1}</span>
      {item.photo_url
        ? <img src={item.photo_url} alt="" className="h-[42px] w-[56px] shrink-0 rounded-[6px] object-cover" />
        : <div className="h-[42px] w-[56px] shrink-0 rounded-[6px] bg-[#F1F5F9]" />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-extrabold text-[#0F172A]">{item.hoarding_id}</div>
        <div className="truncate text-[12px] font-bold text-[#0F172A]">{item.name}</div>
      </div>
      <div className="shrink-0 text-right text-[13px] font-bold text-[#0F172A]">{inr(item.monthly_price)}</div>
      <button type="button" onClick={() => onRemove(item.id)} draggable={false} className="shrink-0 text-[#EF4444] hover:text-red-700" aria-label={`Remove ${item.hoarding_id}`}>
        <X size={15} />
      </button>
    </div>
  );
}

function HorizontalBarRow({ label, count, cost, maxCost }) {
  const pct = maxCost > 0 ? Math.max(4, Math.round((cost / maxCost) * 100)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[12px]">
        <span className="font-semibold text-[#334155]">{label}</span>
        <span className="text-[#64748B]">{count} | {inr(cost)}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[#F1F5F9]">
        <div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniBarRow({ label, count, cost, maxCost }) {
  const pct = maxCost > 0 ? Math.max(4, Math.round((cost / maxCost) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="w-14 shrink-0 font-semibold text-[#334155]">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#F1F5F9]">
        <div className="h-full rounded-full bg-[#60A5FA]" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-24 shrink-0 text-right text-[#64748B]">{count} | {inr(cost)}</span>
    </div>
  );
}

function GenerateFileButton({ icon: Icon, label, color, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-[12px] font-semibold text-[#334155] transition hover:border-[#CBD5E1] hover:bg-slate-50 disabled:opacity-50"
    >
      <Icon size={15} style={{ color }} /> {label}
    </button>
  );
}

function confidenceTone(value) {
  const n = Number(value || 0);
  if (n >= 0.75) return "bg-blue-100 text-blue-700 border-blue-200";
  if (n >= 0.45) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
}

function ConfidenceBadge({ value }) {
  const n = Math.round(Number(value || 0) * 100);
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${confidenceTone(value)}`}>{n}%</span>;
}

const TEXT_STRATEGY_LABELS = {
  replace_entire_text: "Replace this text",
  replace_value_after_label: "Replace value after label",
  replace_currency_value: "Replace amount only",
  append: "Add after existing text",
  none: "Do not change text",
};

const IMAGE_STRATEGY_LABELS = {
  replace_image_keep_box: "Replace image in same box",
  replace_image_keep_crop: "Replace image, keep crop",
  none: "Do not change image",
};

function SmallSelect({ value, options, onChange, labels = {} }) {
  return (
    <Select value={value || options[0]} onValueChange={onChange}>
      <SelectTrigger className="h-8 min-w-0 text-xs [&>span]:truncate"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((option) => <SelectItem key={option} value={option}>{labels[option] || option}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function MappingControl({ label, hint, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="mb-1 flex min-h-4 min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {hint && <span className="truncate text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function DetectedText({ value, fallback = "Template text area" }) {
  return (
    <div className="min-w-0 rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Detected text in template</div>
      <div className="mt-1 truncate text-sm text-slate-700">{value || fallback}</div>
    </div>
  );
}

function fieldOptionForKey(key) {
  return TEMPLATE_FIELD_OPTIONS.find((option) => option.key === key) || null;
}

function TemplateFieldPicker({ value, allowedTypes = ["text", "image"], onSelect }) {
  const selected = fieldOptionForKey(value);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const normalizedQuery = query.trim().toLowerCase();
  const options = TEMPLATE_FIELD_OPTIONS
    .filter((option) => allowedTypes.includes(option.type))
    .filter((option) => {
      if (!normalizedQuery) return true;
      return `${option.key} ${option.label} ${option.category}`.toLowerCase().includes(normalizedQuery);
    })
    .slice(0, 80);

  function choose(option) {
    onSelect(option);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="relative">
      <div className="flex h-8 items-center rounded-md border border-slate-200 bg-white shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
        <Search size={13} className="ml-2 text-slate-400" />
        <input
          value={open ? query : selected?.label || value || ""}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery("");
            setOpen(true);
          }}
          onBlur={() => window.setTimeout(() => setOpen(false), 130)}
          placeholder="Search field"
          className="min-w-0 flex-1 bg-transparent px-2 text-xs font-medium text-slate-900 outline-none placeholder:text-slate-400"
        />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((v) => !v)} className="grid h-8 w-8 place-items-center text-slate-400 hover:text-blue-600" aria-label="Open field picker">
          <ChevronDown size={14} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(option)}
              className={`block w-full rounded-md px-3 py-2 text-left transition hover:bg-blue-50 ${option.key === value ? "bg-blue-50" : ""}`}
            >
              <span className="block truncate text-sm font-semibold text-slate-900">{option.label}</span>
              <span className="mt-0.5 flex min-w-0 items-center gap-2 text-[11px] text-slate-500">
                <span className="truncate font-mono">{option.key}</span>
                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold">{option.category}</span>
              </span>
            </button>
          ))}
          {options.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-400">No matching field</div>}
        </div>
      )}
    </div>
  );
}

function boxStyle(geometry = {}, slide = {}) {
  const slideW = Number(slide.slide_width || geometry.slide_width || 9144000);
  const slideH = Number(slide.slide_height || geometry.slide_height || 5143500);
  const x = Number(geometry.x || 0);
  const y = Number(geometry.y || 0);
  const w = Number(geometry.w || 0);
  const h = Number(geometry.h || 0);
  const left = Math.max(0, Math.min(100, (x / slideW) * 100));
  const top = Math.max(0, Math.min(100, (y / slideH) * 100));
  const right = Math.max(left + 1, Math.min(100, ((x + w) / slideW) * 100));
  const bottom = Math.max(top + 1, Math.min(100, ((y + h) / slideH) * 100));
  return {
    left: `${left}%`,
    top: `${top}%`,
    width: `${right - left}%`,
    height: `${bottom - top}%`,
  };
}

function isDecorativeImageRegion(region = {}) {
  const marker = `${region.role || ""} ${region.field_key || ""} ${region.field_label || ""}`.toLowerCase();
  return marker.includes("decorative") || marker.includes("background") || marker.includes("design element");
}

function regionKey(region) {
  return `${region.kind}-${region.idx}`;
}

function SlideRegionCanvas({ slide, templateId, highlightedRegion, onSelectRegion, showAllRegions = false }) {
  const regions = [
    ...asArray(slide.text_regions).map((region, idx) => ({ ...region, idx, kind: "text", tone: "border-blue-500 bg-blue-500/10", label: region.field_key || "text" })),
    ...asArray(slide.image_regions).map((region, idx) => ({ ...region, idx, kind: "image", tone: "border-sky-500 bg-sky-500/10", label: region.field_key || region.role || "image" })),
    ...asArray(slide.tables).map((region, idx) => ({ ...region, idx, kind: "table", tone: "border-amber-500 bg-amber-500/10", label: region.table_role || "table" })),
  ].filter((region) => {
    if (!region.geometry || Number(region.geometry.w || 0) <= 0 || Number(region.geometry.h || 0) <= 0) return false;
    if (!showAllRegions && !region.replace) return false;
    if (region.kind === "image" && isDecorativeImageRegion(region)) return false;
    return true;
  });
  const previewUrl = slide.preview_path && templateId ? `${API}/proposals/templates/${templateId}/previews/${slide.slide_index}` : "";

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-950/5 p-3">
      <div className="relative aspect-video overflow-hidden rounded-lg bg-white shadow-inner ring-1 ring-slate-200">
        {previewUrl && (
          <img
            src={previewUrl}
            alt={`Slide ${slide.slide_index} preview`}
            className="absolute inset-0 h-full w-full object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        {!previewUrl && <div className="absolute inset-0 grid place-items-center text-sm font-medium text-slate-400">Slide preview unavailable</div>}
        {regions.map((region) => (
          <button
            key={`${region.kind}-${region.idx}-${region.shape_id || region.image_id || region.table_role}`}
            type="button"
            onClick={() => onSelectRegion?.(region)}
            className={`absolute z-10 rounded-md border-2 ${region.tone} ${highlightedRegion === regionKey(region) ? "ring-4 ring-blue-500" : "ring-1 ring-white/70"} transition hover:ring-4 hover:ring-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-500`}
            style={boxStyle(region.geometry, slide)}
            title={`${region.kind}: ${region.label}`}
          >
            <span className="absolute left-2 top-2 max-w-[92%] truncate rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm">
              {region.label}
            </span>
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-medium text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Text</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Image</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Table</span>
      </div>
    </div>
  );
}

function fieldTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase()) || "Template field";
}

function mappingTasks(slide) {
  const tasks = [];
  asArray(slide.text_regions).forEach((region, idx) => {
    if (region.replace) tasks.push({ ...region, idx, kind: "text", title: region.field_label || fieldTitle(region.field_key), subtitle: region.current_text || "Text area" });
  });
  asArray(slide.image_regions).forEach((region, idx) => {
    if (region.replace && !isDecorativeImageRegion(region)) tasks.push({ ...region, idx, kind: "image", title: region.field_label || fieldTitle(region.field_key || region.role), subtitle: "Image area" });
  });
  asArray(slide.tables).forEach((table, idx) => {
    if (table.replace) tasks.push({ ...table, idx, kind: "table", title: fieldTitle(table.table_role || "inventory_table"), subtitle: "Table rows" });
  });
  return tasks;
}

function TemplateMappingReview({ templateId, mapping, onChange }) {
  const warnings = asArray(mapping.warnings).filter((warning) => !isInternalTemplateWarning(warning));
  const slides = asArray(mapping.slides);
  const agencyName = mapping.agency?.name?.value || "";
  const [activeSlideIndex, setActiveSlideIndex] = useState(slides[0]?.slide_index || 1);
  const [highlightedRegion, setHighlightedRegion] = useState("");
  const [showAdvancedMapping, setShowAdvancedMapping] = useState(false);
  const fillPanelRef = useRef(null);
  const previewPanelRef = useRef(null);
  const activeSlide = slides.find((slide) => slide.slide_index === activeSlideIndex) || slides[0];
  const activeTasks = mappingTasks(activeSlide || {});

  useEffect(() => {
    if (slides.length > 0 && !slides.some((slide) => slide.slide_index === activeSlideIndex)) {
      setActiveSlideIndex(slides[0].slide_index);
    }
  }, [activeSlideIndex, slides]);

  useEffect(() => {
    setHighlightedRegion("");
  }, [activeSlideIndex]);

  const patchSlide = (slideIndex, updater) => {
    onChange((draft) => {
      const slide = asArray(draft.slides).find((s) => s.slide_index === slideIndex);
      if (slide) updater(slide);
    });
  };
  const selectRegion = (region, { scrollToFill = false, scrollToPreview = false } = {}) => {
    setHighlightedRegion(regionKey(region));
    setShowAdvancedMapping(true);
    if (scrollToFill) {
      requestAnimationFrame(() => {
        fillPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    if (scrollToPreview) {
      requestAnimationFrame(() => {
        previewPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-white border border-blue-100 p-2">
          <div className="text-slate-500">Agency detected</div>
          <div className="font-semibold text-slate-900 truncate">{agencyName || "Not detected"}</div>
        </div>
        <div className="rounded-md bg-white border border-blue-100 p-2">
          <div className="text-slate-500">Overall confidence</div>
          <div className="mt-1"><ConfidenceBadge value={mapping.overall_confidence} /></div>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {warnings.slice(0, 4).map((w, i) => <div key={`${w}-${i}`}>{w}</div>)}
          {warnings.length > 4 && <div>+{warnings.length - 4} more warnings</div>}
        </div>
      )}

      {slides.length > 0 ? (
        <div className="grid grid-cols-1 xl:grid-cols-[230px_minmax(0,1fr)] gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-2 xl:self-start">
            <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Slides</div>
            <div className="space-y-1 pr-1">
              {slides.map((slide) => {
                const active = activeSlide?.slide_index === slide.slide_index;
                const replaceCount = asArray(slide.text_regions).filter((r) => r.replace).length
                  + asArray(slide.image_regions).filter((r) => r.replace && !isDecorativeImageRegion(r)).length
                  + asArray(slide.tables).filter((r) => r.replace).length;
                return (
                  <button
                    key={slide.slide_index}
                    type="button"
                    onClick={() => setActiveSlideIndex(slide.slide_index)}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition ${active ? "border-blue-300 bg-blue-50 shadow-sm" : "border-transparent hover:border-slate-200 hover:bg-slate-50"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs font-bold text-slate-900">Slide #{slide.slide_index}</span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500">{replaceCount} mapped</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-slate-500">{slide.type || "unknown"}</div>
                    <div className="mt-1 text-[10px] font-medium text-blue-600">{slide.repeat_mode || "none"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-3 items-end">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Slide</div>
                <div className="font-mono text-base font-bold text-slate-900">#{activeSlide.slide_index}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Type</div>
                <SmallSelect value={activeSlide.type || "unknown"} options={SLIDE_TYPES} onChange={(v) => patchSlide(activeSlide.slide_index, (s) => { s.type = v; })} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Repeat</div>
                <SmallSelect value={activeSlide.repeat_mode || "none"} options={REPEAT_MODES} onChange={(v) => patchSlide(activeSlide.slide_index, (s) => { s.repeat_mode = v; })} />
              </div>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                <input type="checkbox" checked={activeSlide.keep_slide !== false} onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.keep_slide = e.target.checked; })} />
                Keep
              </label>
            </div>

            <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
              <div ref={previewPanelRef}>
                <SlideRegionCanvas
                  slide={activeSlide}
                  templateId={templateId}
                  highlightedRegion={highlightedRegion}
                  showAllRegions={showAdvancedMapping}
                  onSelectRegion={(region) => selectRegion(region, { scrollToFill: true })}
                />
              </div>

              <div ref={fillPanelRef} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Hoardexa will fill</div>
                    <div className="text-xs text-slate-500">Click any item to show where it appears.</div>
                  </div>
                  <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-slate-500">{activeTasks.length}</span>
                </div>
                <div className="mt-3 space-y-2">
                  {activeTasks.map((task) => {
                    const active = highlightedRegion === regionKey(task);
                    return (
                      <button
                        key={regionKey(task)}
                        type="button"
                        onClick={() => selectRegion(task, { scrollToPreview: true })}
                        className={`w-full rounded-lg border bg-white px-3 py-2 text-left transition ${active ? "border-blue-300 shadow-sm ring-2 ring-blue-100" : "border-slate-200 hover:border-blue-200"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-slate-900">{task.title}</span>
                          <ConfidenceBadge value={task.confidence || 0.5} />
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-500">{task.subtitle}</div>
                      </button>
                    );
                  })}
                  {activeTasks.length === 0 && (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-sm text-slate-400">
                      No fillable fields detected on this slide.
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" className="mt-3 w-full" onClick={() => setShowAdvancedMapping((v) => !v)}>
                  <ChevronDown size={14} className={showAdvancedMapping ? "rotate-180 transition" : "transition"} />
                  {showAdvancedMapping ? "Hide advanced mapping" : "Advanced mapping"}
                </Button>
              </div>
            </div>

            {showAdvancedMapping && asArray(activeSlide.text_regions).length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-600">Text Regions</div>
                {asArray(activeSlide.text_regions).map((region, idx) => (
                  <div key={`${region.shape_id}-${idx}`} className={`rounded-lg border p-3 space-y-3 ${highlightedRegion === regionKey({ ...region, idx, kind: "text" }) ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100" : "border-slate-100 bg-slate-50"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <DetectedText value={region.current_text || region.shape_id} />
                      <div className="shrink-0 pt-1"><ConfidenceBadge value={region.confidence} /></div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(220px,1.5fr)_minmax(180px,1fr)_minmax(110px,.55fr)] xl:items-end">
                      <MappingControl label="Proposal value" hint="what Hoardexa should insert">
                        <TemplateFieldPicker
                          value={region.field_key || ""}
                          allowedTypes={["text"]}
                          onSelect={(option) => patchSlide(activeSlide.slide_index, (s) => {
                            s.text_regions[idx].field_key = option.key;
                            s.text_regions[idx].field_label = option.label;
                            s.text_regions[idx].field_category = option.category;
                          })}
                        />
                      </MappingControl>
                      <MappingControl label="How to fill">
                        <SmallSelect value={region.replace_strategy || "none"} options={TEXT_STRATEGIES} labels={TEXT_STRATEGY_LABELS} onChange={(v) => patchSlide(activeSlide.slide_index, (s) => { s.text_regions[idx].replace_strategy = v; })} />
                      </MappingControl>
                      <MappingControl label="Fill">
                        <label className="flex h-8 min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">
                          <input type="checkbox" checked={Boolean(region.replace)} onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.text_regions[idx].replace = e.target.checked; })} />
                          Enabled
                        </label>
                      </MappingControl>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showAdvancedMapping && asArray(activeSlide.image_regions).length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-600">Image Regions</div>
                {asArray(activeSlide.image_regions).map((region, idx) => (
                  <div key={`${region.image_id}-${idx}`} className={`grid grid-cols-1 gap-3 rounded-lg border p-3 xl:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(110px,.55fr)] xl:items-end ${highlightedRegion === regionKey({ ...region, idx, kind: "image" }) ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100" : "border-slate-100 bg-slate-50"}`}>
                    <MappingControl label="Proposal image" hint="what Hoardexa should insert">
                      <TemplateFieldPicker
                        value={region.field_key || ""}
                        allowedTypes={["image"]}
                        onSelect={(option) => patchSlide(activeSlide.slide_index, (s) => {
                          s.image_regions[idx].field_key = option.key;
                          s.image_regions[idx].field_label = option.label;
                          s.image_regions[idx].field_category = option.category;
                        })}
                      />
                    </MappingControl>
                    <MappingControl label="How to fill">
                      <SmallSelect value={region.replace_strategy || "none"} options={IMAGE_STRATEGIES} labels={IMAGE_STRATEGY_LABELS} onChange={(v) => patchSlide(activeSlide.slide_index, (s) => { s.image_regions[idx].replace_strategy = v; })} />
                    </MappingControl>
                    <MappingControl label="Fill">
                      <label className="flex h-8 min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">
                        <input type="checkbox" checked={Boolean(region.replace)} onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.image_regions[idx].replace = e.target.checked; })} />
                        Enabled
                      </label>
                    </MappingControl>
                  </div>
                ))}
              </div>
            )}

            {showAdvancedMapping && asArray(activeSlide.tables).length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-600">Tables</div>
                {asArray(activeSlide.tables).map((table, tableIdx) => (
                  <div key={`${table.shape_id}-${tableIdx}`} className={`rounded-md border p-2 space-y-2 ${highlightedRegion === regionKey({ ...table, idx: tableIdx, kind: "table" }) ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100" : "border-slate-100 bg-slate-50"}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <Input className="h-8 text-xs" value={table.table_role || ""} placeholder="table_role" onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.tables[tableIdx].table_role = e.target.value; })} />
                      <Input className="h-8 text-xs" value={table.data_scope || ""} placeholder="data_scope" onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.tables[tableIdx].data_scope = e.target.value; })} />
                      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                        <input type="checkbox" checked={Boolean(table.repeat_rows)} onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.tables[tableIdx].repeat_rows = e.target.checked; })} />
                        Repeat rows
                      </label>
                      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                        <input type="checkbox" checked={Boolean(table.replace)} onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.tables[tableIdx].replace = e.target.checked; })} />
                        Replace
                      </label>
                    </div>
                    <div className="space-y-1">
                      {asArray(table.columns).map((col, colIdx) => (
                        <div key={`${table.shape_id}-${colIdx}`} className="grid grid-cols-1 sm:grid-cols-[70px_1fr_minmax(180px,1.2fr)_1fr] gap-2">
                          <div className="text-xs text-slate-500 pt-2">Col {col.index}</div>
                          <Input className="h-8 text-xs" value={col.header_text || ""} placeholder="Header" onChange={(e) => patchSlide(activeSlide.slide_index, (s) => { s.tables[tableIdx].columns[colIdx].header_text = e.target.value; })} />
                          <TemplateFieldPicker
                            value={col.field_key || ""}
                            allowedTypes={["text"]}
                            onSelect={(option) => patchSlide(activeSlide.slide_index, (s) => {
                              s.tables[tableIdx].columns[colIdx].field_key = option.key;
                              s.tables[tableIdx].columns[colIdx].field_label = option.label;
                              s.tables[tableIdx].columns[colIdx].field_category = option.category;
                              s.tables[tableIdx].columns[colIdx].semantic_role = option.key;
                            })}
                          />
                          <Input className="h-8 text-xs bg-slate-50" value={col.semantic_role || col.field_key || ""} placeholder="semantic role" readOnly />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-400">No slide mapping yet. Analyze the template first.</div>
      )}
    </div>
  );
}


